/* @jsx h */
import { defineComponent, h, onMounted, onUnmounted, ref } from "vue";

type Obstacle = {
  left: number;
  top: number;
  width: number;
  height: number;
  type: string;
};
type Footprint = {
  x: number;
  y: number;
  age: number;
  facing: number;
  message?: string;
};
type SoundBubble = {
  x: number;
  y: number;
  text: string;
  timer: number;
  maxTimer: number;
};
type Portal = {
  x: number;
  y: number;
  radius: number;
  phase: "opening" | "closing";
  timer: number;
  isExit: boolean;
};
type Particle = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  color: string;
  size: number;
};
type TrailPoint = { x: number; y: number; age: number };
type BandMember = {
  x: number;
  y: number;
  role: "guitar" | "drums" | "keys" | "singer";
  armCycle: number;
  bobCycle: number;
  facing: number;
};
type Zombie = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  legCycle: number;
  facing: number;
  wobble: number;
  shade: number; // darkness 0-1 for different zombie shades
};
type Activity =
  | "sipping"
  | "stretching"
  | "pushups"
  | "phone"
  | "selfie"
  | "picnic"
  | "jumping_jacks"
  | "sitting"
  | "chef_kiss"
  | "scratch_head"
  | "point"
  | "shrug"
  | "facepalm"
  | "happy_dance"
  | "look_both_ways"
  | "sprint";
type QuestIcon = { emoji: string; label: string; activity: Activity };

const QUESTS: QuestIcon[] = [
  { emoji: "⛰️", label: "Mountain", activity: "stretching" },
  { emoji: "🌲", label: "Tree", activity: "picnic" },
  { emoji: "🏴", label: "Flag", activity: "jumping_jacks" },
  { emoji: "👸", label: "Princess", activity: "selfie" },
  { emoji: "💰", label: "Gold", activity: "sipping" },
  { emoji: "📦", label: "Treasure", activity: "sitting" },
  { emoji: "🔥", label: "Campfire", activity: "phone" },
  { emoji: "⭐", label: "Star", activity: "pushups" },
  { emoji: "🎪", label: "Circus", activity: "jumping_jacks" },
  { emoji: "🏰", label: "Castle", activity: "selfie" },
  { emoji: "🎣", label: "Fishing", activity: "sitting" },
  { emoji: "🍕", label: "Pizza", activity: "sipping" },
  { emoji: "🎸", label: "Band", activity: "facepalm" }, // Band story
  { emoji: "🧟", label: "Zombies", activity: "look_both_ways" }, // Zombie horde
];
type Phase =
  | "walking"
  | "falling"
  | "vertigo"
  | "gettingUp"
  | "stopped"
  | "enterPortal"
  | "exitPortal"
  | "stopping"
  | "activity"
  | "bandStory"
  | "zombieHorde";

const ACTIVITIES: Activity[] = [
  "sipping",
  "stretching",
  "pushups",
  "phone",
  "selfie",
  "picnic",
  "jumping_jacks",
  "sitting",
  "chef_kiss",
  "scratch_head",
  "point",
  "shrug",
  "facepalm",
  "happy_dance",
  "look_both_ways",
  "sprint",
];

export const FloorPlanWalker = defineComponent({
  name: "FloorPlanWalker",
  props: ["gridRef", "floorPlan"],
  setup(props) {
    const canvasRef = ref<HTMLCanvasElement | null>(null);
    let animHandle = 0;
    let themeObserver: MutationObserver | undefined;
    const themeColorCache = new Map<string, string>();

    const themedColor = (token: string, alpha?: number) => {
      let color = themeColorCache.get(token);
      if (!color) {
        const source = canvasRef.value ?? document.documentElement;
        color = getComputedStyle(source).getPropertyValue(token).trim();
        themeColorCache.set(token, color);
      }
      if (alpha === undefined) return color;

      const hex = color.match(/^#([\da-f]{6})$/i)?.[1];
      if (hex) {
        return `rgba(${parseInt(hex.slice(0, 2), 16)},${parseInt(hex.slice(2, 4), 16)},${parseInt(hex.slice(4, 6), 16)},${alpha})`;
      }
      const channels = color.match(
        /^rgba?\(\s*([\d.]+)[, ]+\s*([\d.]+)[, ]+\s*([\d.]+)/i,
      );
      if (channels) {
        return `rgba(${channels[1]},${channels[2]},${channels[3]},${alpha})`;
      }
      return color;
    };

    const getGrid = (): HTMLElement | null => {
      const gr = props.gridRef as any;
      return (
        gr && gr.value !== undefined ? gr.value : gr
      ) as HTMLElement | null;
    };

    const w = {
      x: 0,
      y: 0,
      vx: 0,
      vy: 0,
      targetX: 100,
      targetY: 100,
      speed: 0.18,
      phase: "walking" as Phase,
      phaseTimer: 0,
      legCycle: 0,
      facing: 1,
      fallAngle: 0,
      getUpProgress: 0,
      vertigoAngle: 0,
      stuckCounter: 0,
      lastX: 0,
      lastY: 0,
      initialized: false,
      stepAccum: 0,
      velocityX: 0,
      velocityY: 0,
      heading: Math.random() * Math.PI * 2,
      wanderTimer: 0,
      subTargetX: 0,
      subTargetY: 0,
      fallSpots: [] as { x: number; y: number }[],
      // Squash/stretch (Iteration 3)
      scaleX: 1,
      scaleY: 1,
      targetScaleX: 1,
      targetScaleY: 1,
      // Eye openness (Iteration 3)
      eyeOpenness: 1.0,
      // Secondary animation (Iteration 5)
      tieAngle: 0,
      coatFlutter: 0,
      pocketSway: 0,
      lineWobblePhase: 0,
      // Fidgeting (Iteration 5)
      fidgetTimer: 0,
      fidgetAction: "" as string,
      // Idle wobble (Iteration 5)
      idleWobble: 0,
      // Anticipation (Iteration 3)
      anticipationTimer: 0,
      isAnticipating: false,
      // Activity
      currentActivity: "sipping" as Activity,
      activityCycle: 0,
      activityCooldown: 0,
      // Emoji bubble
      bubble: "" as string,
      bubbleTimer: 0,
      // Quest
      questIndex: -1,
      lastQuestIndex: -1,
    };

    // Active quest target
    const quest = {
      active: false,
      x: 0,
      y: 0,
      icon: QUESTS[0],
      bobCycle: 0,
      appearTimer: 0, // fade in
      reachDistance: 28,
      age: 0, // frames since quest spawned
      maxAge: 3600, // ~60 seconds at 60fps — give up and spawn new quest
    };

    const footprints: Footprint[] = [];
    const MAX_PRINTS = 50;
    const PRINT_FADE = 350;
    const portals: Portal[] = [];
    const particles: Particle[] = [];
    const trailPoints: TrailPoint[] = [];
    const bandMembers: BandMember[] = [];
    const zombies: Zombie[] = [];
    const soundBubbles: SoundBubble[] = [];
    let bandStoryPhase: "approaching" | "performing" | "leaving" =
      "approaching";
    let bandStoryTimer = 0;
    const BAND_SPRITE_SIZE = 2.8;

    const getObstacles = (): Obstacle[] => {
      const grid = getGrid();
      if (!grid) return [];
      const gridRect = grid.getBoundingClientRect();
      const plan = (props.floorPlan as any)?.plan;
      if (!plan) return [];
      const obstacles: Obstacle[] = [];
      for (const t of plan) {
        const cell = grid.querySelector(
          `[data-row='${t.rowStart}'][data-col='${t.colStart}']`,
        ) as HTMLElement;
        if (!cell) continue;
        const cellRect = cell.getBoundingClientRect();
        obstacles.push({
          left: cellRect.left - gridRect.left,
          top: cellRect.top - gridRect.top,
          width: cellRect.width * (t.colEnd - t.colStart + 1),
          height: cellRect.height * (t.rowEnd - t.rowStart + 1),
          type: t.type || "table",
        });
      }
      return obstacles;
    };

    const hitTest = (
      x: number,
      y: number,
      obs: Obstacle[],
      margin = 10,
    ): Obstacle | null => {
      for (const o of obs) {
        if (
          x >= o.left - margin &&
          x <= o.left + o.width + margin &&
          y >= o.top - margin &&
          y <= o.top + o.height + margin
        )
          return o;
      }
      return null;
    };

    const wasNearFall = (x: number, y: number): boolean => {
      for (const f of w.fallSpots) {
        if (Math.sqrt((x - f.x) ** 2 + (y - f.y) ** 2) < 40) return true;
      }
      return false;
    };

    const pickSafeTarget = (cw: number, ch: number, obs: Obstacle[]) => {
      for (let i = 0; i < 50; i++) {
        const tx = 20 + Math.random() * (cw - 40);
        const ty = 20 + Math.random() * (ch - 40);
        if (!hitTest(tx, ty, obs, 14)) {
          w.targetX = tx;
          w.targetY = ty;
          return;
        }
      }
      w.targetX = cw / 2;
      w.targetY = ch / 2;
    };

    const findSafeSpot = (
      cw: number,
      ch: number,
      obs: Obstacle[],
      away?: { x: number; y: number },
    ) => {
      for (let i = 0; i < 80; i++) {
        const tx = 20 + Math.random() * (cw - 40);
        const ty = 20 + Math.random() * (ch - 40);
        if (!hitTest(tx, ty, obs, 14)) {
          if (away && Math.sqrt((tx - away.x) ** 2 + (ty - away.y) ** 2) < 100)
            continue;
          return { x: tx, y: ty };
        }
      }
      return { x: cw / 2, y: ch / 2 };
    };

    // ── Drawing helpers ──

    const drawPortal = (ctx: CanvasRenderingContext2D, p: Portal) => {
      ctx.save();
      ctx.translate(p.x, p.y);
      const progress = p.phase === "opening" ? p.timer / 20 : 1 - p.timer / 15;
      const r = p.radius * Math.max(0, Math.min(1, progress));
      const grad = ctx.createRadialGradient(0, 0, 0, 0, 0, r);
      grad.addColorStop(0, themedColor("--floor-plan-walker-portal-core", 0.9));
      grad.addColorStop(
        0.6,
        themedColor("--floor-plan-walker-portal-middle", 0.7),
      );
      grad.addColorStop(
        0.85,
        themedColor("--floor-plan-walker-portal-edge", 0.3),
      );
      grad.addColorStop(1, themedColor("--floor-plan-walker-portal-edge", 0));
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.ellipse(0, 2, r, r * 0.4, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    };

    const drawStars = (
      ctx: CanvasRenderingContext2D,
      x: number,
      y: number,
      angle: number,
    ) => {
      ctx.save();
      ctx.translate(x, y - 20);
      for (let i = 0; i < 4; i++) {
        const a = angle + (i * Math.PI * 2) / 4;
        const sx = Math.cos(a) * 10,
          sy = Math.sin(a) * 5;
        const sz = 1.6;
        ctx.fillStyle = themedColor(
          "--floor-plan-walker-accent",
          0.6 + Math.sin(a) * 0.3,
        );
        ctx.beginPath();
        ctx.moveTo(sx, sy - sz);
        ctx.lineTo(sx + sz * 0.3, sy);
        ctx.lineTo(sx, sy + sz);
        ctx.lineTo(sx - sz * 0.3, sy);
        ctx.closePath();
        ctx.fill();
      }
      ctx.restore();
    };

    const drawFootprint = (ctx: CanvasRenderingContext2D, fp: Footprint) => {
      const alpha = Math.max(0, (1 - fp.age / PRINT_FADE) * 0.22);
      ctx.save();
      ctx.translate(fp.x, fp.y);
      ctx.scale(fp.facing, 1);
      ctx.fillStyle = themedColor("--floor-plan-walker-ink", alpha);
      // Shoe sole shape
      ctx.beginPath();
      ctx.ellipse(0, 0, 2.0, 1.0, 0.1, 0, Math.PI * 2);
      ctx.fill();
      // Heel
      ctx.beginPath();
      ctx.ellipse(-1.6, 0.1, 0.9, 0.75, 0, 0, Math.PI * 2);
      ctx.fill();
      // Toe detail
      ctx.fillStyle = themedColor("--floor-plan-walker-ink", alpha * 0.5);
      ctx.beginPath();
      ctx.ellipse(1.2, -0.1, 0.5, 0.4, 0, 0, Math.PI * 2);
      ctx.fill();
      // Message trail (Iteration 5)
      if (fp.message) {
        ctx.font = "6px sans-serif";
        ctx.fillStyle = themedColor("--floor-plan-walker-warm", alpha * 0.6);
        ctx.textAlign = "center";
        ctx.fillText(fp.message, 0, 4);
      }
      ctx.restore();
    };

    const drawBubble = (
      ctx: CanvasRenderingContext2D,
      x: number,
      y: number,
      text: string,
      timer: number,
      maxTimer: number,
    ) => {
      const alpha =
        timer < 20
          ? timer / 20
          : timer > maxTimer - 20
            ? (maxTimer - timer) / 20
            : 1;
      const bounce = Math.sin(timer * 0.1) * 1.5;
      ctx.save();
      ctx.globalAlpha = alpha * 0.9;
      ctx.font = "12px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(text, x, y - 20 + bounce);
      ctx.restore();
    };

    const drawSoundBubble = (ctx: CanvasRenderingContext2D) => {
      for (const sb of soundBubbles) {
        const alpha =
          sb.timer < 15
            ? sb.timer / 15
            : sb.timer > sb.maxTimer - 15
              ? (sb.maxTimer - sb.timer) / 15
              : 1;
        const scale = 0.8 + Math.sin(sb.timer * 0.1) * 0.1;
        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.font = `bold ${Math.floor(14 * scale)}px sans-serif`;
        ctx.textAlign = "center";
        ctx.fillStyle = themedColor("--floor-plan-walker-warm", 0.95);
        ctx.strokeStyle = themedColor("--floor-plan-walker-shadow", 0.3);
        ctx.lineWidth = 2;
        const txt = sb.text;
        const tw = ctx.measureText(txt).width;
        ctx.beginPath();
        ctx.roundRect(sb.x - tw / 2 - 6, sb.y - 22, tw + 12, 18, 4);
        ctx.fill();
        ctx.stroke();
        ctx.fillStyle = themedColor("--floor-plan-walker-shadow", 0.9);
        ctx.fillText(txt, sb.x, sb.y - 8);
        ctx.restore();
      }
    };

    const spawnSoundBubble = (x: number, y: number) => {
      const sounds = [
        "WHOA!",
        "HMM?",
        "AHA!",
        "WHOOPS!",
        "YEAH!",
        "HUH?",
        "OOPS!",
      ];
      if (soundBubbles.length > 3) return;
      soundBubbles.push({
        x,
        y,
        text: sounds[Math.floor(Math.random() * sounds.length)],
        timer: 0,
        maxTimer: 50 + Math.floor(Math.random() * 30),
      });
    };

    const drawParticles = (ctx: CanvasRenderingContext2D) => {
      for (const p of particles) {
        const lifeRatio = p.life / p.maxLife;
        const alpha = lifeRatio * 0.8;
        const size = p.size * lifeRatio;
        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.fillStyle = p.color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, size, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }
    };

    const drawTrail = (ctx: CanvasRenderingContext2D) => {
      if (trailPoints.length < 2) return;
      ctx.save();
      for (let i = 0; i < trailPoints.length; i++) {
        const tp = trailPoints[i];
        const alpha = Math.max(0, 1 - tp.age / 40) * 0.3;
        const size = 2 * Math.max(0, 1 - tp.age / 40);
        ctx.globalAlpha = alpha;
        ctx.fillStyle = themedColor("--floor-plan-walker-accent");
        ctx.beginPath();
        ctx.arc(tp.x, tp.y, size, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
    };

    const spawnSparkle = (
      x: number,
      y: number,
      color = themedColor("--floor-plan-walker-accent"),
    ) => {
      if (particles.length > 50) return;
      const angle = Math.random() * Math.PI * 2;
      const speed = 0.5 + Math.random() * 1.5;
      particles.push({
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life: 30 + Math.random() * 20,
        maxLife: 50,
        color,
        size: 1.5 + Math.random() * 2,
      });
    };

    const spawnQuest = (cw: number, ch: number, obs: Obstacle[]) => {
      // Pick a different quest each time
      let idx = Math.floor(Math.random() * QUESTS.length);
      let attempts = 0;
      while (idx === w.lastQuestIndex && attempts < 10) {
        idx = Math.floor(Math.random() * QUESTS.length);
        attempts++;
      }
      w.lastQuestIndex = idx;
      quest.icon = QUESTS[idx];

      // Place target in a safe spot, away from walker
      const spot = findSafeSpot(cw, ch, obs, { x: w.x, y: w.y });
      quest.x = spot.x;
      quest.y = spot.y;
      quest.active = true;
      quest.appearTimer = 0;
      quest.bobCycle = 0;
      quest.age = 0;

      // Set walker target to quest location
      w.targetX = quest.x;
      w.targetY = quest.y;
    };

    const drawQuestTarget = (ctx: CanvasRenderingContext2D) => {
      if (!quest.active) return;
      quest.bobCycle++;
      quest.appearTimer = Math.min(30, quest.appearTimer + 1);

      const alpha = quest.appearTimer / 30;
      const bob = Math.sin(quest.bobCycle * 0.05) * 3;
      const pulse = 1 + Math.sin(quest.bobCycle * 0.08) * 0.08;

      ctx.save();
      ctx.translate(quest.x, quest.y + bob);
      ctx.globalAlpha = alpha;
      ctx.scale(pulse, pulse);

      // Glowing circle beneath
      const glow = ctx.createRadialGradient(0, 6, 0, 0, 6, 14);
      glow.addColorStop(0, themedColor("--floor-plan-walker-accent", 0.2));
      glow.addColorStop(1, themedColor("--floor-plan-walker-accent", 0));
      ctx.fillStyle = glow;
      ctx.beginPath();
      ctx.ellipse(0, 6, 14, 5, 0, 0, Math.PI * 2);
      ctx.fill();

      // Icon
      ctx.font = "14px sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillStyle = themedColor("--floor-plan-walker-ink");
      ctx.fillText(quest.icon.emoji, 0, -2);

      // Small pointer arrow below (▼)
      ctx.fillStyle = themedColor("--floor-plan-walker-accent", alpha * 0.6);
      ctx.beginPath();
      ctx.moveTo(-4, 10);
      ctx.lineTo(4, 10);
      ctx.lineTo(0, 16);
      ctx.closePath();
      ctx.fill();

      ctx.restore();
    };

    // ── Shared body parts ──

    const S = 3.2; // scale (1.3x original)
    let COL = themedColor("--floor-plan-walker-ink");
    let COL_DIM = themedColor("--floor-plan-walker-ink-dim");

    // Physics constants (Iteration 3)
    const SQUASH_STRETCH_FACTOR = 0.15;
    const ANTICIPATION_FRAMES = 10;
    const MOMENTUM_FRICTION = 0.92;
    const PARTICLE_TRAIL_COUNT = 8;

    // Iteration 5: Hand-drawn polish
    const WOBBLE_AMOUNT = 0.04; // line wobble intensity
    const WOBBLE_SPEED = 0.08; // wobble frequency
    const MAX_FIDGET_DELAY = 180; // max frames between fidget actions

    // Easing functions (Phase 2 additions)
    const easeOutQuint = (t: number) => 1 - Math.pow(1 - t, 5);
    const easeInOutBack = (t: number) => {
      const c1 = 1.70158,
        c2 = c1 * 1.525;
      return t < 0.5
        ? (Math.pow(2 * t, 2) * ((c2 + 1) * 2 * t - c2)) / 2
        : (Math.pow(2 * t - 2, 2) * ((c2 + 1) * (t * 2 - 2) + c2) + 2) / 2;
    };
    const anticipationEase = (t: number, strength = 0.3) => {
      if (t < 0.2) return -strength * Math.sin(t * Math.PI * 5) * (t / 0.2);
      return 1 - Math.pow(1 - (t - 0.2) / 0.8, 3);
    };

    const drawHat = (ctx: CanvasRenderingContext2D, y: number) => {
      ctx.fillStyle = COL;
      // Brim — wider, slightly curved
      ctx.beginPath();
      ctx.ellipse(0, -S * 4.8 + y, S * 2.1, S * 0.38, 0, 0, Math.PI * 2);
      ctx.fill();
      // Crown — tapered fedora shape
      ctx.beginPath();
      ctx.moveTo(-S * 0.95, -S * 4.8 + y);
      ctx.quadraticCurveTo(-S * 0.85, -S * 5.8 + y, -S * 0.5, -S * 6.1 + y);
      ctx.lineTo(S * 0.5, -S * 6.1 + y);
      ctx.quadraticCurveTo(S * 0.85, -S * 5.8 + y, S * 0.95, -S * 4.8 + y);
      ctx.closePath();
      ctx.fill();
      // Dent in crown
      ctx.strokeStyle = themedColor("--floor-plan-walker-detail-dim", 0.2);
      ctx.lineWidth = S * 0.12;
      ctx.beginPath();
      ctx.moveTo(-S * 0.25, -S * 6.1 + y);
      ctx.quadraticCurveTo(0, -S * 5.7 + y, S * 0.25, -S * 6.1 + y);
      ctx.stroke();
      // Hat band
      ctx.strokeStyle = themedColor("--floor-plan-walker-detail", 0.6);
      ctx.lineWidth = S * 0.18;
      ctx.beginPath();
      ctx.moveTo(-S * 0.9, -S * 5.0 + y);
      ctx.lineTo(S * 0.9, -S * 5.0 + y);
      ctx.stroke();
      // Band buckle/knot
      ctx.fillStyle = themedColor("--floor-plan-walker-detail", 0.5);
      ctx.beginPath();
      ctx.arc(-S * 0.3, -S * 5.0 + y, S * 0.12, 0, Math.PI * 2);
      ctx.fill();
    };

    const drawHead = (ctx: CanvasRenderingContext2D, y: number) => {
      ctx.fillStyle = COL;
      ctx.beginPath();
      ctx.arc(0, -S * 3.9 + y, S * 0.9, 0, Math.PI * 2);
      ctx.fill();
      // Subtle face details — tiny dot eyes
      ctx.fillStyle = themedColor("--floor-plan-walker-shadow", 0.3);
      ctx.beginPath();
      ctx.arc(-S * 0.25, -S * 4.0 + y, S * 0.08, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(S * 0.25, -S * 4.0 + y, S * 0.08, 0, Math.PI * 2);
      ctx.fill();
    };

    // Wobble line helper for sketchy hand-drawn feel (Iteration 5)
    const wobbleLine = (
      ctx: CanvasRenderingContext2D,
      x1: number,
      y1: number,
      x2: number,
      y2: number,
      phase: number,
      amplitude = WOBBLE_AMOUNT,
    ) => {
      const dx = x2 - x1,
        dy = y2 - y1;
      const len = Math.sqrt(dx * dx + dy * dy);
      const steps = Math.max(2, Math.floor(len / 8));
      ctx.beginPath();
      ctx.moveTo(
        x1 + Math.sin(phase) * amplitude * S,
        y1 + Math.cos(phase * 1.3) * amplitude * S,
      );
      for (let i = 1; i <= steps; i++) {
        const t = i / steps;
        const wobble =
          Math.sin(phase + t * Math.PI * 2) * amplitude * S * (1 - t * 0.5);
        const px = x1 + dx * t + Math.cos(phase + t) * wobble;
        const py = y1 + dy * t + Math.sin(phase + t * 1.7) * wobble;
        ctx.lineTo(px, py);
      }
      ctx.stroke();
    };

    const drawTorso = (ctx: CanvasRenderingContext2D, y: number) => {
      const phase = w.lineWobblePhase;
      // Main body line (with wobble)
      ctx.strokeStyle = COL;
      ctx.lineWidth = S * 0.5;
      wobbleLine(ctx, 0, -S * 3.0 + y, 0, S * 0.3 + y, phase);
      // Jacket outline (V lapels)
      ctx.lineWidth = S * 0.18;
      ctx.beginPath();
      ctx.moveTo(0, -S * 2.7 + y);
      ctx.lineTo(-S * 0.45, -S * 1.7 + y);
      ctx.moveTo(0, -S * 2.7 + y);
      ctx.lineTo(S * 0.45, -S * 1.7 + y);
      ctx.stroke();
      // Tie (with flutter - Iteration 5)
      const tieSwing = w.tieAngle * S * 0.3;
      ctx.strokeStyle = themedColor("--floor-plan-walker-detail", 0.5);
      ctx.lineWidth = S * 0.1;
      ctx.beginPath();
      ctx.moveTo(0, -S * 2.65 + y);
      ctx.quadraticCurveTo(
        -S * 0.08 + tieSwing * 0.5,
        -S * 1.8 + y + tieSwing * 0.3,
        tieSwing,
        -S * 1.0 + y + tieSwing,
      );
      ctx.stroke();
      // Buttons
      ctx.fillStyle = COL;
      ctx.beginPath();
      ctx.arc(0, -S * 1.35 + y, S * 0.1, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(0, -S * 0.75 + y, S * 0.1, 0, Math.PI * 2);
      ctx.fill();
      // Pocket square (left breast - with sway)
      const pocketSway = w.pocketSway * S * 0.15;
      ctx.strokeStyle = themedColor("--floor-plan-walker-detail", 0.4);
      ctx.lineWidth = S * 0.08;
      ctx.beginPath();
      ctx.moveTo(-S * 0.3 + pocketSway, -S * 2.1 + y);
      ctx.lineTo(-S * 0.2 + pocketSway * 1.3, -S * 2.35 + y);
      ctx.lineTo(-S * 0.1 + pocketSway * 0.8, -S * 2.15 + y);
      ctx.stroke();
      // Shoulders (with coat flutter - Iteration 5)
      const coatFlutter = w.coatFlutter * S * 0.1;
      ctx.strokeStyle = COL;
      ctx.lineWidth = S * 0.4;
      ctx.beginPath();
      ctx.moveTo(-S * 1.2 - coatFlutter, -S * 2.4 + y);
      ctx.lineTo(S * 1.2 + coatFlutter, -S * 2.4 + y);
      ctx.stroke();
    };

    const drawLegs = (
      ctx: CanvasRenderingContext2D,
      y: number,
      legSwing: number,
      spread = 0,
    ) => {
      // Hips / belt line
      ctx.strokeStyle = COL;
      ctx.lineWidth = S * 0.38;
      ctx.beginPath();
      ctx.moveTo(-S * (0.55 + spread), S * 0.3 + y);
      ctx.lineTo(S * (0.55 + spread), S * 0.3 + y);
      ctx.stroke();
      // Belt buckle
      ctx.fillStyle = themedColor("--floor-plan-walker-detail", 0.35);
      ctx.beginPath();
      ctx.arc(0, S * 0.3 + y, S * 0.1, 0, Math.PI * 2);
      ctx.fill();
      // Left leg (trouser)
      ctx.strokeStyle = COL;
      ctx.lineWidth = S * 0.34;
      const lKneeX = -S * (0.8 + spread) - legSwing * S * 0.35;
      const lKneeY = S * 1.6 + y;
      const lFootX = -S * (0.65 + spread) - legSwing * S * 0.4;
      const lFootY = S * 2.8;
      ctx.beginPath();
      ctx.moveTo(-S * (0.55 + spread), S * 0.3 + y);
      ctx.lineTo(lKneeX, lKneeY);
      ctx.lineTo(lFootX, lFootY);
      ctx.stroke();
      // Right leg
      const rKneeX = S * (0.8 + spread) + legSwing * S * 0.35;
      const rKneeY = S * 1.6 + y;
      const rFootX = S * (0.65 + spread) + legSwing * S * 0.4;
      const rFootY = S * 2.8;
      ctx.beginPath();
      ctx.moveTo(S * (0.55 + spread), S * 0.3 + y);
      ctx.lineTo(rKneeX, rKneeY);
      ctx.lineTo(rFootX, rFootY);
      ctx.stroke();
      // Shoes — dress shoes with sole detail
      ctx.lineWidth = S * 0.28;
      // Left shoe
      ctx.beginPath();
      ctx.moveTo(lFootX, lFootY);
      ctx.lineTo(lFootX + S * 0.4, lFootY + S * 0.25);
      ctx.stroke();
      // Sole line
      ctx.strokeStyle = themedColor("--floor-plan-walker-detail-dim");
      ctx.lineWidth = S * 0.08;
      ctx.beginPath();
      ctx.moveTo(lFootX - S * 0.05, lFootY + S * 0.12);
      ctx.lineTo(lFootX + S * 0.35, lFootY + S * 0.32);
      ctx.stroke();
      // Right shoe
      ctx.strokeStyle = COL;
      ctx.lineWidth = S * 0.28;
      ctx.beginPath();
      ctx.moveTo(rFootX, rFootY);
      ctx.lineTo(rFootX + S * 0.4, rFootY + S * 0.25);
      ctx.stroke();
      ctx.strokeStyle = themedColor("--floor-plan-walker-detail-dim");
      ctx.lineWidth = S * 0.08;
      ctx.beginPath();
      ctx.moveTo(rFootX - S * 0.05, rFootY + S * 0.12);
      ctx.lineTo(rFootX + S * 0.35, rFootY + S * 0.32);
      ctx.stroke();
    };

    const drawGlass = (
      ctx: CanvasRenderingContext2D,
      gx: number,
      gy: number,
    ) => {
      // Wine glass bowl
      ctx.strokeStyle = COL_DIM;
      ctx.lineWidth = S * 0.14;
      ctx.beginPath();
      ctx.moveTo(gx - S * 0.35, gy - S * 0.7);
      ctx.quadraticCurveTo(gx - S * 0.35, gy - S * 0.1, gx, gy - S * 0.05);
      ctx.quadraticCurveTo(
        gx + S * 0.35,
        gy - S * 0.1,
        gx + S * 0.35,
        gy - S * 0.7,
      );
      ctx.stroke();
      // Wine fill
      ctx.fillStyle = themedColor("--floor-plan-walker-danger", 0.3);
      ctx.beginPath();
      ctx.moveTo(gx - S * 0.28, gy - S * 0.4);
      ctx.quadraticCurveTo(gx, gy - S * 0.1, gx + S * 0.28, gy - S * 0.4);
      ctx.closePath();
      ctx.fill();
      // Stem
      ctx.strokeStyle = COL_DIM;
      ctx.lineWidth = S * 0.08;
      ctx.beginPath();
      ctx.moveTo(gx, gy - S * 0.05);
      ctx.lineTo(gx, gy + S * 0.3);
      ctx.stroke();
      // Base
      ctx.lineWidth = S * 0.12;
      ctx.beginPath();
      ctx.moveTo(gx - S * 0.22, gy + S * 0.3);
      ctx.lineTo(gx + S * 0.22, gy + S * 0.3);
      ctx.stroke();
      // Highlight on bowl
      ctx.strokeStyle = themedColor("--floor-plan-walker-ink", 0.15);
      ctx.lineWidth = S * 0.05;
      ctx.beginPath();
      ctx.moveTo(gx + S * 0.15, gy - S * 0.6);
      ctx.lineTo(gx + S * 0.18, gy - S * 0.35);
      ctx.stroke();
    };

    // ── Per-activity drawing ──

    const drawWalking = (ctx: CanvasRenderingContext2D, cycle: number) => {
      const arm = Math.sin(cycle) * 0.45;
      const leg = Math.sin(cycle) * 0.55;
      drawHat(ctx, 0);
      drawHead(ctx, 0);
      drawTorso(ctx, 0);
      // Left arm + glass
      ctx.strokeStyle = COL;
      ctx.lineWidth = S * 0.32;
      ctx.beginPath();
      ctx.moveTo(-S * 1.2, -S * 2.4);
      ctx.lineTo(-S * 1.5, -S * 1.5 + arm * S * 0.2);
      ctx.lineTo(-S * 1.8, -S * 1.8 + arm * S * 0.15);
      ctx.stroke();
      drawGlass(ctx, -S * 1.8, -S * 1.8 + arm * S * 0.15);
      // Right arm
      ctx.strokeStyle = COL;
      ctx.lineWidth = S * 0.32;
      ctx.beginPath();
      ctx.moveTo(S * 1.2, -S * 2.4);
      ctx.lineTo(S * 1.6, -S * 1.3 - arm * S * 0.6);
      ctx.lineTo(S * 1.3, -S * 0.2 - arm * S * 0.4);
      ctx.stroke();
      drawLegs(ctx, 0, leg);
    };

    const drawSipping = (ctx: CanvasRenderingContext2D, c: number) => {
      // Phased: 0-40 raise glass, 40-80 sip (head back), 80-120 lower, 120-160 satisfied nod, repeat
      const phase = c % 160;
      let glassY = -S * 1.8; // default resting position
      let headTilt = 0;
      let bodyNod = 0;

      if (phase < 40) {
        // Raising glass smoothly
        const t = phase / 40;
        glassY = -S * 1.8 + t * -S * 2.2;
      } else if (phase < 80) {
        // Sipping — glass at mouth, head tilted back
        glassY = -S * 4.0;
        headTilt = -0.15;
        bodyNod = Math.sin((phase - 40) * 0.15) * 0.03; // slight swallow motion
      } else if (phase < 120) {
        // Lowering glass
        const t = (phase - 80) / 40;
        glassY = -S * 4.0 + t * S * 2.2;
        headTilt = -0.15 * (1 - t);
      } else {
        // Satisfied nod — head bobs
        bodyNod = Math.sin((phase - 120) * 0.2) * 0.06;
      }

      ctx.rotate(bodyNod);
      drawHat(ctx, 0);
      ctx.save();
      ctx.rotate(headTilt);
      drawHead(ctx, 0);
      ctx.restore();
      drawTorso(ctx, 0);
      // Left arm raises glass
      ctx.strokeStyle = COL;
      ctx.lineWidth = S * 0.32;
      ctx.beginPath();
      ctx.moveTo(-S * 1.2, -S * 2.4);
      ctx.lineTo(-S * 0.8, glassY + S * 0.5);
      ctx.stroke();
      drawGlass(ctx, -S * 0.8, glassY);
      // Right arm on hip
      ctx.strokeStyle = COL;
      ctx.lineWidth = S * 0.32;
      ctx.beginPath();
      ctx.moveTo(S * 1.2, -S * 2.4);
      ctx.lineTo(S * 1.0, -S * 1.0);
      ctx.lineTo(S * 0.5, -S * 0.2);
      ctx.stroke();
      drawLegs(ctx, 0, 0);
    };

    const drawJumpingJacks = (ctx: CanvasRenderingContext2D, c: number) => {
      // Fast cycle: arms+legs spread on odd beats, together on even
      // Last 30 frames: slow down + wipe brow
      const totalCycles = 8;
      const cycleLen = 25;
      const exerciseEnd = totalCycles * cycleLen;

      if (c < exerciseEnd) {
        const t = (c % cycleLen) / cycleLen;
        const up = Math.abs(Math.sin(t * Math.PI)); // 0→1→0 per cycle
        const bounce = -up * 5;
        drawHat(ctx, bounce);
        drawHead(ctx, bounce);
        drawTorso(ctx, bounce);
        ctx.strokeStyle = COL;
        ctx.lineWidth = S * 0.32;
        const armAngle = up * 1.3;
        ctx.beginPath();
        ctx.moveTo(-S * 1.2, -S * 2.4 + bounce);
        ctx.lineTo(
          -S * 1.2 - Math.sin(armAngle) * S * 2.2,
          -S * 2.4 - Math.cos(armAngle) * S * 2.2 + bounce,
        );
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(S * 1.2, -S * 2.4 + bounce);
        ctx.lineTo(
          S * 1.2 + Math.sin(armAngle) * S * 2.2,
          -S * 2.4 - Math.cos(armAngle) * S * 2.2 + bounce,
        );
        ctx.stroke();
        drawLegs(ctx, bounce, 0, up * 0.8);
      } else {
        // Tired: standing, wiping brow
        drawHat(ctx, 0);
        drawHead(ctx, 0);
        drawTorso(ctx, 0);
        ctx.strokeStyle = COL;
        ctx.lineWidth = S * 0.32;
        // Right arm wiping forehead
        const wipe = Math.sin((c - exerciseEnd) * 0.1) * S * 0.5;
        ctx.beginPath();
        ctx.moveTo(S * 1.2, -S * 2.4);
        ctx.lineTo(S * 0.5 + wipe, -S * 4.2);
        ctx.stroke();
        // Left arm on hip
        ctx.beginPath();
        ctx.moveTo(-S * 1.2, -S * 2.4);
        ctx.lineTo(-S * 1.0, -S * 0.5);
        ctx.stroke();
        drawLegs(ctx, 0, 0, 0.15);
      }
    };

    const drawStretching = (ctx: CanvasRenderingContext2D, c: number) => {
      // Phased: 0-60 bend left, 60-120 bend right, 120-180 reach up, 180-240 touch toes
      const phase = c % 240;
      let bend = 0;
      let armLY = -S * 2.4,
        armLX = -S * 1.2;
      let armRY = -S * 2.4,
        armRX = S * 1.2;
      let crouch = 0;

      if (phase < 60) {
        bend = Math.sin((phase / 60) * Math.PI) * 0.25;
        armLX = -S * 0.5;
        armLY = -S * 5.5;
        armRX = S * 1.0;
        armRY = -S * 0.5;
      } else if (phase < 120) {
        bend = -Math.sin(((phase - 60) / 60) * Math.PI) * 0.25;
        armRX = S * 0.5;
        armRY = -S * 5.5;
        armLX = -S * 1.0;
        armLY = -S * 0.5;
      } else if (phase < 180) {
        const t = Math.sin(((phase - 120) / 60) * Math.PI);
        armLX = -S * 0.8;
        armLY = -S * 2.4 - t * S * 3.5;
        armRX = S * 0.8;
        armRY = -S * 2.4 - t * S * 3.5;
      } else {
        const t = Math.sin(((phase - 180) / 60) * Math.PI);
        crouch = t * 0.3;
        bend = t * 0.5;
        armLX = -S * 0.5;
        armLY = -S * 1 + t * S * 3;
        armRX = S * 0.5;
        armRY = -S * 1 + t * S * 3;
      }

      ctx.rotate(bend);
      if (crouch > 0) ctx.scale(1, 1 - crouch * 0.3);
      drawHat(ctx, 0);
      drawHead(ctx, 0);
      drawTorso(ctx, 0);
      ctx.strokeStyle = COL;
      ctx.lineWidth = S * 0.32;
      ctx.beginPath();
      ctx.moveTo(-S * 1.2, -S * 2.4);
      ctx.lineTo(armLX, armLY);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(S * 1.2, -S * 2.4);
      ctx.lineTo(armRX, armRY);
      ctx.stroke();
      drawLegs(ctx, 0, 0, 0.3);
    };

    const drawPushups = (ctx: CanvasRenderingContext2D, c: number) => {
      // Phased: 0-20 get down, 20-180 pushups (4 reps), 180-200 struggle, 200+ stand up
      const getDownEnd = 20,
        repsEnd = 180,
        struggleEnd = 200;

      if (c < getDownEnd) {
        // Getting down — rotating body forward
        const t = c / getDownEnd;
        ctx.rotate(t * Math.PI * 0.42);
        ctx.translate(0, -t * S * 1.5);
        drawHat(ctx, 0);
        drawHead(ctx, 0);
        drawTorso(ctx, 0);
        ctx.strokeStyle = COL;
        ctx.lineWidth = S * 0.32;
        ctx.beginPath();
        ctx.moveTo(-S * 1.2, -S * 2.4);
        ctx.lineTo(-S * 1.8, -S * 0.5);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(S * 1.2, -S * 2.4);
        ctx.lineTo(S * 1.8, -S * 0.5);
        ctx.stroke();
        drawLegs(ctx, 0, 0);
      } else if (c < repsEnd) {
        // Pushups — body horizontal, arms pump
        const rep = Math.sin((c - getDownEnd) * 0.08);
        const armBend = 0.5 + rep * 0.5;
        const bodyH = S * 0.5 + armBend * S * 1.2;
        ctx.rotate(Math.PI * 0.42);
        ctx.translate(0, -bodyH);
        ctx.strokeStyle = COL;
        ctx.lineWidth = S * 0.5;
        ctx.beginPath();
        ctx.moveTo(-S * 3, 0);
        ctx.lineTo(S * 3, 0);
        ctx.stroke();
        ctx.fillStyle = COL;
        ctx.beginPath();
        ctx.arc(-S * 3.5, -S * 0.3, S * 0.8, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.ellipse(-S * 3.5, -S * 1.0, S * 1.5, S * 0.3, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.lineWidth = S * 0.32;
        ctx.beginPath();
        ctx.moveTo(-S * 1.5, 0);
        ctx.lineTo(-S * 1.5, bodyH);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(S * 0.5, 0);
        ctx.lineTo(S * 0.5, bodyH);
        ctx.stroke();
        ctx.lineWidth = S * 0.34;
        ctx.beginPath();
        ctx.moveTo(S * 3, 0);
        ctx.lineTo(S * 3.5, S * 0.3);
        ctx.stroke();
      } else if (c < struggleEnd) {
        // Struggling — body shakes, barely pushing up
        const shake = Math.sin(c * 0.5) * 0.03;
        ctx.rotate(Math.PI * 0.42 + shake);
        ctx.translate(0, -S * 0.6);
        ctx.strokeStyle = COL;
        ctx.lineWidth = S * 0.5;
        ctx.beginPath();
        ctx.moveTo(-S * 3, 0);
        ctx.lineTo(S * 3, 0);
        ctx.stroke();
        ctx.fillStyle = COL;
        ctx.beginPath();
        ctx.arc(-S * 3.5, -S * 0.3, S * 0.8, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.ellipse(-S * 3.5, -S * 1.0, S * 1.5, S * 0.3, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.lineWidth = S * 0.32;
        ctx.beginPath();
        ctx.moveTo(-S * 1.5, 0);
        ctx.lineTo(-S * 1.5, S * 0.6);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(S * 0.5, 0);
        ctx.lineTo(S * 0.5, S * 0.6);
        ctx.stroke();
        // Sweat drops
        ctx.fillStyle = themedColor("--floor-plan-walker-accent-alt", 0.5);
        ctx.beginPath();
        ctx.arc(
          -S * 4.2,
          -S * 0.5 + Math.sin(c * 0.2) * S * 0.3,
          S * 0.2,
          0,
          Math.PI * 2,
        );
        ctx.fill();
      } else {
        // Standing back up
        const t = Math.min(1, (c - struggleEnd) / 30);
        ctx.rotate((1 - t) * Math.PI * 0.42);
        drawHat(ctx, 0);
        drawHead(ctx, 0);
        drawTorso(ctx, 0);
        ctx.strokeStyle = COL;
        ctx.lineWidth = S * 0.32;
        ctx.beginPath();
        ctx.moveTo(-S * 1.2, -S * 2.4);
        ctx.lineTo(-S * 1.5, -S * 1.0);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(S * 1.2, -S * 2.4);
        ctx.lineTo(S * 1.5, -S * 1.0);
        ctx.stroke();
        drawLegs(ctx, 0, 0);
      }
    };

    const drawPhone = (ctx: CanvasRenderingContext2D, c: number) => {
      // Phased: pacing slightly, head nodding, free hand gestures
      const pace = Math.sin(c * 0.02) * S * 0.8;
      const nod = Math.sin(c * 0.08) * 0.04;
      const gesture = Math.sin(c * 0.06);
      ctx.translate(pace, 0);
      drawHat(ctx, 0);
      ctx.save();
      ctx.rotate(nod);
      drawHead(ctx, 0);
      ctx.restore();
      drawTorso(ctx, 0);
      ctx.strokeStyle = COL;
      ctx.lineWidth = S * 0.32;
      // Left arm: phone to ear
      ctx.beginPath();
      ctx.moveTo(-S * 1.2, -S * 2.4);
      ctx.lineTo(-S * 0.6, -S * 3.4);
      ctx.lineTo(-S * 0.2, -S * 4.0);
      ctx.stroke();
      // Phone
      ctx.strokeStyle = COL_DIM;
      ctx.lineWidth = S * 0.15;
      ctx.strokeRect(-S * 0.4, -S * 4.5, S * 0.35, S * 0.65);
      // Right arm: gesturing while talking
      ctx.strokeStyle = COL;
      ctx.lineWidth = S * 0.32;
      ctx.beginPath();
      ctx.moveTo(S * 1.2, -S * 2.4);
      ctx.lineTo(S * 1.8, -S * 1.8 + gesture * S * 0.6);
      ctx.lineTo(S * 2.0, -S * 1.2 + gesture * S * 0.4);
      ctx.stroke();
      // Walking legs (slow pace)
      const legSwing = Math.sin(c * 0.04) * 0.2;
      drawLegs(ctx, 0, legSwing);
    };

    const drawSelfie = (ctx: CanvasRenderingContext2D, c: number) => {
      // Phased: 0-60 pose1+flash, 60-90 check photo, 90-150 pose2+flash, 150-180 thumbs up
      const phase = c % 180;

      drawHat(ctx, 0);
      drawHead(ctx, 0);
      drawTorso(ctx, 0);
      ctx.strokeStyle = COL;
      ctx.lineWidth = S * 0.32;

      if (phase < 60) {
        // Pose 1: arm extended, peace sign
        ctx.beginPath();
        ctx.moveTo(-S * 1.2, -S * 2.4);
        ctx.lineTo(-S * 3.5, -S * 3.2);
        ctx.stroke();
        ctx.strokeStyle = COL_DIM;
        ctx.lineWidth = S * 0.15;
        ctx.strokeRect(-S * 3.9, -S * 3.7, S * 0.5, S * 0.8);
        // Flash at frame 45-55
        if (phase > 45 && phase < 55) {
          ctx.fillStyle = themedColor(
            "--floor-plan-walker-warm",
            0.8 - (phase - 45) * 0.08,
          );
          ctx.beginPath();
          ctx.arc(
            -S * 3.65,
            -S * 3.3,
            S * 0.5 + (phase - 45) * 0.2,
            0,
            Math.PI * 2,
          );
          ctx.fill();
        }
        // Peace sign
        ctx.strokeStyle = COL;
        ctx.lineWidth = S * 0.32;
        ctx.beginPath();
        ctx.moveTo(S * 1.2, -S * 2.4);
        ctx.lineTo(S * 1.8, -S * 4.0);
        ctx.stroke();
        ctx.lineWidth = S * 0.18;
        ctx.beginPath();
        ctx.moveTo(S * 1.8, -S * 4.0);
        ctx.lineTo(S * 1.5, -S * 4.8);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(S * 1.8, -S * 4.0);
        ctx.lineTo(S * 2.1, -S * 4.8);
        ctx.stroke();
      } else if (phase < 90) {
        // Checking photo — both hands on phone, looking down
        ctx.beginPath();
        ctx.moveTo(-S * 1.2, -S * 2.4);
        ctx.lineTo(-S * 0.5, -S * 1.5);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(S * 1.2, -S * 2.4);
        ctx.lineTo(S * 0.5, -S * 1.5);
        ctx.stroke();
        ctx.strokeStyle = COL_DIM;
        ctx.lineWidth = S * 0.15;
        ctx.strokeRect(-S * 0.4, -S * 1.8, S * 0.8, S * 1.0);
      } else if (phase < 150) {
        // Pose 2: different pose — leaning, hand on hip
        const lean = 0.08;
        ctx.rotate(lean);
        ctx.beginPath();
        ctx.moveTo(-S * 1.2, -S * 2.4);
        ctx.lineTo(-S * 3.5, -S * 2.8);
        ctx.stroke();
        ctx.strokeStyle = COL_DIM;
        ctx.lineWidth = S * 0.15;
        ctx.strokeRect(-S * 3.9, -S * 3.3, S * 0.5, S * 0.8);
        if (phase > 135 && phase < 145) {
          ctx.fillStyle = themedColor(
            "--floor-plan-walker-warm",
            0.8 - (phase - 135) * 0.08,
          );
          ctx.beginPath();
          ctx.arc(-S * 3.65, -S * 2.9, S * 0.5, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.strokeStyle = COL;
        ctx.lineWidth = S * 0.32;
        ctx.beginPath();
        ctx.moveTo(S * 1.2, -S * 2.4);
        ctx.lineTo(S * 1.0, -S * 0.5);
        ctx.stroke();
      } else {
        // Thumbs up — satisfied
        ctx.beginPath();
        ctx.moveTo(-S * 1.2, -S * 2.4);
        ctx.lineTo(-S * 1.5, -S * 1.0);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(S * 1.2, -S * 2.4);
        ctx.lineTo(S * 2.0, -S * 3.5);
        ctx.stroke();
        // Thumb
        ctx.lineWidth = S * 0.2;
        ctx.beginPath();
        ctx.moveTo(S * 2.0, -S * 3.5);
        ctx.lineTo(S * 2.0, -S * 4.2);
        ctx.stroke();
      }
      drawLegs(ctx, 0, 0);
    };

    const drawPicnic = (ctx: CanvasRenderingContext2D, c: number) => {
      // Phased: 0-30 spread blanket, 30-60 sit down, 60+ eating/drinking cycle
      ctx.translate(0, S * 2);

      // Blanket (always visible after phase 0)
      if (c > 10) {
        const blanketScale = Math.min(1, (c - 10) / 20);
        ctx.fillStyle = themedColor("--floor-plan-walker-danger", 0.2);
        ctx.beginPath();
        ctx.ellipse(
          0,
          S * 2,
          S * 4 * blanketScale,
          S * 1.2 * blanketScale,
          0,
          0,
          Math.PI * 2,
        );
        ctx.fill();
        ctx.strokeStyle = themedColor("--floor-plan-walker-danger", 0.15);
        ctx.lineWidth = 0.3;
        ctx.beginPath();
        ctx.ellipse(
          0,
          S * 2,
          S * 4 * blanketScale,
          S * 1.2 * blanketScale,
          0,
          0,
          Math.PI * 2,
        );
        ctx.stroke();
      }

      if (c < 30) {
        // Spreading blanket — bending down
        const t = c / 30;
        const bend = Math.sin(t * Math.PI) * 0.3;
        ctx.rotate(bend);
        drawHat(ctx, 0);
        drawHead(ctx, 0);
        drawTorso(ctx, 0);
        ctx.strokeStyle = COL;
        ctx.lineWidth = S * 0.32;
        ctx.beginPath();
        ctx.moveTo(-S * 1.2, -S * 2.4);
        ctx.lineTo(-S * 2.5, S * 0.5);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(S * 1.2, -S * 2.4);
        ctx.lineTo(S * 2.5, S * 0.5);
        ctx.stroke();
        drawLegs(ctx, 0, 0, 0.2);
      } else {
        // Sitting and eating
        const eatPhase = (c - 30) % 80;
        drawHat(ctx, 0);
        drawHead(ctx, 0);
        drawTorso(ctx, 0);
        ctx.strokeStyle = COL;
        ctx.lineWidth = S * 0.32;

        if (eatPhase < 40) {
          // Reaching for food
          const reach = Math.sin((eatPhase / 40) * Math.PI) * S;
          ctx.beginPath();
          ctx.moveTo(-S * 1.2, -S * 2.4);
          ctx.lineTo(-S * 2.0, -S * 0.5 + reach * 0.3);
          ctx.stroke();
        } else {
          // Eating — hand to mouth
          const eat = Math.sin(((eatPhase - 40) / 40) * Math.PI);
          ctx.beginPath();
          ctx.moveTo(-S * 1.2, -S * 2.4);
          ctx.lineTo(-S * 0.5, -S * 3.5 - eat * S * 0.5);
          ctx.stroke();
        }
        ctx.beginPath();
        ctx.moveTo(S * 1.2, -S * 2.4);
        ctx.lineTo(S * 1.8, -S * 1.0);
        ctx.stroke();

        // Crossed legs
        ctx.lineWidth = S * 0.34;
        ctx.beginPath();
        ctx.moveTo(-S * 0.5, S * 0.3);
        ctx.lineTo(S * 0.5, S * 1.5);
        ctx.lineTo(S * 1.0, S * 1.0);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(S * 0.5, S * 0.3);
        ctx.lineTo(-S * 0.5, S * 1.5);
        ctx.lineTo(-S * 1.0, S * 1.0);
        ctx.stroke();

        // Food items
        ctx.fillStyle = themedColor("--floor-plan-walker-warm", 0.4);
        ctx.beginPath();
        ctx.arc(S * 2, S * 0.5, S * 0.4, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = themedColor("--floor-plan-walker-danger", 0.35);
        ctx.beginPath();
        ctx.arc(-S * 1.5, S * 1.0, S * 0.35, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = themedColor("--floor-plan-walker-success", 0.3);
        ctx.beginPath();
        ctx.arc(S * 0.5, S * 1.5, S * 0.3, 0, Math.PI * 2);
        ctx.fill();
      }
    };

    const drawSitting = (ctx: CanvasRenderingContext2D, c: number) => {
      // Phased: 0-25 sit down, 25-50 unfold paper, 50+ reading with page turns
      ctx.translate(0, S * 0.8);

      if (c < 25) {
        // Sitting down
        const t = c / 25;
        ctx.translate(0, t * S * 0.5);
        drawHat(ctx, 0);
        drawHead(ctx, 0);
        drawTorso(ctx, 0);
        ctx.strokeStyle = COL;
        ctx.lineWidth = S * 0.32;
        ctx.beginPath();
        ctx.moveTo(-S * 1.2, -S * 2.4);
        ctx.lineTo(-S * 1.5, -S * 1.0);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(S * 1.2, -S * 2.4);
        ctx.lineTo(S * 1.5, -S * 1.0);
        ctx.stroke();
        // Legs bending
        ctx.lineWidth = S * 0.34;
        const bend = t;
        ctx.beginPath();
        ctx.moveTo(-S * 0.55, S * 0.3);
        ctx.lineTo(-S * (0.55 + bend * 0.65), S * 0.3);
        ctx.lineTo(-S * (0.55 + bend * 0.65), S * (0.3 + bend * 1.7));
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(S * 0.55, S * 0.3);
        ctx.lineTo(S * (0.55 + bend * 0.65), S * 0.3);
        ctx.lineTo(S * (0.55 + bend * 0.65), S * (0.3 + bend * 1.7));
        ctx.stroke();
      } else {
        // Seated, reading
        const readPhase = (c - 25) % 120;
        const headLook =
          readPhase > 100 ? Math.sin((readPhase - 100) * 0.15) * 0.1 : 0; // look over paper

        drawHat(ctx, 0);
        ctx.save();
        ctx.rotate(headLook);
        drawHead(ctx, 0);
        ctx.restore();
        drawTorso(ctx, 0);
        ctx.strokeStyle = COL;
        ctx.lineWidth = S * 0.32;
        // Arms holding newspaper
        ctx.beginPath();
        ctx.moveTo(-S * 1.2, -S * 2.4);
        ctx.lineTo(-S * 2.0, -S * 1.5);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(S * 1.2, -S * 2.4);
        ctx.lineTo(S * 2.0, -S * 1.5);
        ctx.stroke();
        // Newspaper
        const paperW = S * 4.0;
        const paperH = S * 1.5;
        // Page turn effect at frame 60-80
        const turnOffset =
          readPhase > 60 && readPhase < 80
            ? Math.sin(((readPhase - 60) / 20) * Math.PI) * S * 0.8
            : 0;
        ctx.fillStyle = themedColor("--floor-plan-walker-ink", 0.07);
        ctx.fillRect(-paperW / 2, -S * 2.0, paperW, paperH);
        ctx.strokeStyle = COL_DIM;
        ctx.lineWidth = S * 0.12;
        ctx.strokeRect(-paperW / 2, -S * 2.0, paperW, paperH);
        // Page turning
        if (turnOffset > 0) {
          ctx.fillStyle = themedColor("--floor-plan-walker-ink", 0.1);
          ctx.beginPath();
          ctx.moveTo(paperW / 2, -S * 2.0);
          ctx.lineTo(paperW / 2 - turnOffset, -S * 2.0);
          ctx.lineTo(paperW / 2 - turnOffset, -S * 2.0 + paperH);
          ctx.lineTo(paperW / 2, -S * 2.0 + paperH);
          ctx.closePath();
          ctx.fill();
        }
        // Text lines
        ctx.strokeStyle = themedColor("--floor-plan-walker-ink", 0.12);
        ctx.lineWidth = 0.3;
        for (let i = 0; i < 4; i++) {
          ctx.beginPath();
          ctx.moveTo(-S * 1.7, -S * 1.7 + i * S * 0.35);
          ctx.lineTo(S * 1.7, -S * 1.7 + i * S * 0.35);
          ctx.stroke();
        }
        // Seated legs
        ctx.strokeStyle = COL;
        ctx.lineWidth = S * 0.34;
        ctx.beginPath();
        ctx.moveTo(-S * 0.55, S * 0.3);
        ctx.lineTo(-S * 1.2, S * 0.3);
        ctx.lineTo(-S * 1.2, S * 2.0);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(S * 0.55, S * 0.3);
        ctx.lineTo(S * 1.2, S * 0.3);
        ctx.lineTo(S * 1.2, S * 2.0);
        ctx.stroke();
        ctx.lineWidth = S * 0.28;
        ctx.beginPath();
        ctx.moveTo(-S * 1.2, S * 2.0);
        ctx.lineTo(-S * 0.8, S * 2.2);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(S * 1.2, S * 2.0);
        ctx.lineTo(S * 1.6, S * 2.2);
        ctx.stroke();
      }
    };

    // ── New Iteration 3 Activities ──

    const drawChefKiss = (ctx: CanvasRenderingContext2D, c: number) => {
      // Phased: 0-20 raise hand, 20-35 blow kiss, 35-50 flourish, 50-70 lower hand, repeat
      const phase = c % 70;
      let armAngle = 0;
      let kissOffset = 0;
      let flourish = 0;

      if (phase < 20) {
        const t = phase / 20;
        armAngle = easeOutQuint(t) * (-Math.PI * 0.7);
      } else if (phase < 35) {
        const t = (phase - 20) / 15;
        armAngle = -Math.PI * 0.7;
        kissOffset = Math.sin(t * Math.PI) * S * 1.5;
        flourish = t;
      } else if (phase < 50) {
        const t = (phase - 35) / 15;
        armAngle = -Math.PI * 0.7 + t * 0.3;
        flourish = 1 - t;
      } else {
        const t = (phase - 50) / 20;
        armAngle = -Math.PI * 0.4 * (1 - easeOutQuint(t));
      }

      drawHat(ctx, 0);
      ctx.save();
      ctx.rotate(Math.sin(c * 0.05) * 0.02); // slight head bob
      drawHead(ctx, 0);
      ctx.restore();
      drawTorso(ctx, 0);

      // Left arm - chef's kiss gesture
      ctx.strokeStyle = COL;
      ctx.lineWidth = S * 0.32;
      const handX = -S * 1.2 + Math.cos(armAngle) * S * 2.0;
      const handY = -S * 2.4 + Math.sin(armAngle) * S * 2.0;
      ctx.beginPath();
      ctx.moveTo(-S * 1.2, -S * 2.4);
      ctx.lineTo(handX, handY);
      ctx.stroke();

      // Kiss particles
      if (flourish > 0 && c % 8 < 4) {
        ctx.fillStyle = themedColor(
          "--floor-plan-walker-accent-alt",
          flourish * 0.6,
        );
        ctx.beginPath();
        ctx.arc(
          handX - S * 0.3,
          handY - kissOffset - S * 0.5,
          S * 0.15,
          0,
          Math.PI * 2,
        );
        ctx.fill();
      }

      // Right arm on hip
      ctx.beginPath();
      ctx.moveTo(S * 1.2, -S * 2.4);
      ctx.lineTo(S * 1.0, -S * 1.0);
      ctx.lineTo(S * 0.5, -S * 0.2);
      ctx.stroke();

      drawLegs(ctx, 0, 0);
    };

    const drawScratchHead = (ctx: CanvasRenderingContext2D, c: number) => {
      // Phased: alternating arms scratching head, confused expression
      const phase = c % 60;
      const armIndex = Math.floor(phase / 30);
      const scratchPhase = phase % 30;
      const scratch = Math.sin(scratchPhase * 0.3) * S * 0.4;
      const bend = 0.15 + Math.sin(scratchPhase * 0.15) * 0.05;

      ctx.rotate(bend);
      drawHat(ctx, 0);

      // Head tilting with scratch
      ctx.save();
      ctx.rotate(-0.1 + Math.sin(scratchPhase * 0.2) * 0.05);
      drawHead(ctx, 0);
      ctx.restore();

      drawTorso(ctx, 0);

      ctx.strokeStyle = COL;
      ctx.lineWidth = S * 0.32;

      if (armIndex === 0) {
        // Left arm scratching
        ctx.beginPath();
        ctx.moveTo(-S * 1.2, -S * 2.4);
        ctx.lineTo(-S * 0.5, -S * 4.5 + scratch);
        ctx.stroke();
        // Scratching fingers
        ctx.lineWidth = S * 0.15;
        for (let i = 0; i < 3; i++) {
          ctx.beginPath();
          ctx.moveTo(-S * 0.5 + i * S * 0.12, -S * 4.5 + scratch);
          ctx.lineTo(-S * 0.5 + i * S * 0.12, -S * 4.8 + scratch);
          ctx.stroke();
        }
        // Right arm hanging
        ctx.lineWidth = S * 0.32;
        ctx.beginPath();
        ctx.moveTo(S * 1.2, -S * 2.4);
        ctx.lineTo(S * 1.5, -S * 1.0);
        ctx.stroke();
      } else {
        // Right arm scratching
        ctx.beginPath();
        ctx.moveTo(S * 1.2, -S * 2.4);
        ctx.lineTo(S * 0.5, -S * 4.5 + scratch);
        ctx.stroke();
        ctx.lineWidth = S * 0.15;
        for (let i = 0; i < 3; i++) {
          ctx.beginPath();
          ctx.moveTo(S * 0.5 - i * S * 0.12, -S * 4.5 + scratch);
          ctx.lineTo(S * 0.5 - i * S * 0.12, -S * 4.8 + scratch);
          ctx.stroke();
        }
        ctx.lineWidth = S * 0.32;
        ctx.beginPath();
        ctx.moveTo(-S * 1.2, -S * 2.4);
        ctx.lineTo(-S * 1.5, -S * 1.0);
        ctx.stroke();
      }

      drawLegs(ctx, 0, 0);
    };

    const drawPoint = (ctx: CanvasRenderingContext2D, c: number) => {
      // Phased: 0-40 point forward, 40-60 lower, 60-100 point different direction, repeat
      const phase = c % 100;
      let armAngle = 0;
      let pointDir = 1;
      let bodyLean = 0;

      if (phase < 40) {
        const t = phase / 40;
        armAngle = easeOutQuint(t) * (-Math.PI * 0.5);
        bodyLean = easeOutQuint(t) * 0.08;
      } else if (phase < 60) {
        const t = (phase - 40) / 20;
        armAngle = -Math.PI * 0.5 * (1 - easeOutQuint(t));
        bodyLean = 0.08 * (1 - easeOutQuint(t));
      } else {
        pointDir = -1;
        const t = (phase - 60) / 40;
        armAngle = easeOutQuint(t) * (Math.PI * 0.3);
        bodyLean = -easeOutQuint(t) * 0.05;
      }

      ctx.rotate(bodyLean);
      drawHat(ctx, 0);

      ctx.save();
      ctx.rotate(pointDir * 0.05);
      drawHead(ctx, 0);
      ctx.restore();

      drawTorso(ctx, 0);

      // Pointing arm
      ctx.strokeStyle = COL;
      ctx.lineWidth = S * 0.32;
      const handX = pointDir * S * 1.2 + Math.cos(armAngle) * S * 2.5;
      const handY = -S * 2.4 + Math.sin(armAngle) * S * 2.5;
      ctx.beginPath();
      ctx.moveTo(pointDir * S * 1.2, -S * 2.4);
      ctx.lineTo(handX, handY);
      ctx.stroke();

      // Extended index finger
      ctx.lineWidth = S * 0.18;
      ctx.beginPath();
      ctx.moveTo(handX, handY);
      ctx.lineTo(handX + pointDir * S * 0.8, handY - S * 0.3);
      ctx.stroke();

      // Other hand
      ctx.lineWidth = S * 0.32;
      ctx.beginPath();
      ctx.moveTo(-pointDir * S * 1.2, -S * 2.4);
      ctx.lineTo(-pointDir * S * 0.8, -S * 1.2);
      ctx.stroke();

      drawLegs(ctx, 0, 0, 0.1);
    };

    const drawShrug = (ctx: CanvasRenderingContext2D, c: number) => {
      // Phased: shoulders rise and fall, palms out
      const phase = c % 80;
      let shoulderRise = 0;
      let palmOut = 0;
      let headTilt = 0;

      if (phase < 30) {
        const t = phase / 30;
        shoulderRise = easeOutQuint(t) * S * 1.5;
        palmOut = easeOutQuint(t) * S * 0.8;
        headTilt = easeOutQuint(t) * 0.1;
      } else if (phase < 50) {
        shoulderRise = S * 1.5;
        palmOut = S * 0.8;
        headTilt = 0.1 + Math.sin((phase - 30) * 0.1) * 0.05;
      } else {
        const t = (phase - 50) / 30;
        shoulderRise = S * 1.5 * (1 - easeOutQuint(t));
        palmOut = S * 0.8 * (1 - easeOutQuint(t));
        headTilt = 0.1 * (1 - easeOutQuint(t));
      }

      ctx.save();
      ctx.rotate(headTilt);
      drawHat(ctx, shoulderRise);
      drawHead(ctx, shoulderRise);
      ctx.restore();

      ctx.save();
      ctx.translate(0, shoulderRise * 0.5);
      drawTorso(ctx, 0);
      ctx.restore();

      ctx.strokeStyle = COL;
      ctx.lineWidth = S * 0.32;

      // Left arm - shoulder height, palm out
      ctx.beginPath();
      ctx.moveTo(-S * 1.2, -S * 2.4 + shoulderRise * 0.5);
      ctx.lineTo(-S * 2.0, -S * 2.0 + shoulderRise);
      ctx.lineTo(-S * 2.5 - palmOut, -S * 2.0 + shoulderRise);
      ctx.stroke();

      // Palm open
      ctx.lineWidth = S * 0.15;
      ctx.beginPath();
      ctx.moveTo(-S * 2.5 - palmOut, -S * 2.0 + shoulderRise);
      ctx.lineTo(-S * 2.8 - palmOut, -S * 2.2 + shoulderRise);
      ctx.lineTo(-S * 2.3 - palmOut, -S * 2.3 + shoulderRise);
      ctx.stroke();

      // Right arm - mirrored
      ctx.lineWidth = S * 0.32;
      ctx.beginPath();
      ctx.moveTo(S * 1.2, -S * 2.4 + shoulderRise * 0.5);
      ctx.lineTo(S * 2.0, -S * 2.0 + shoulderRise);
      ctx.lineTo(S * 2.5 + palmOut, -S * 2.0 + shoulderRise);
      ctx.stroke();

      ctx.lineWidth = S * 0.15;
      ctx.beginPath();
      ctx.moveTo(S * 2.5 + palmOut, -S * 2.0 + shoulderRise);
      ctx.lineTo(S * 2.8 + palmOut, -S * 2.2 + shoulderRise);
      ctx.lineTo(S * 2.3 + palmOut, -S * 2.3 + shoulderRise);
      ctx.stroke();

      drawLegs(ctx, 0, 0, 0.2);
    };

    // ── Band Story Drawing (Iteration 4) ──

    const drawBandMember = (
      ctx: CanvasRenderingContext2D,
      member: BandMember,
    ) => {
      ctx.save();
      ctx.translate(member.x, member.y);
      ctx.scale(member.facing, 1);

      const bob = Math.sin(member.bobCycle) * 1.5;
      const armSwing = Math.sin(member.armCycle) * 0.5;

      // Simplified stick figure for band
      ctx.strokeStyle = themedColor("--floor-plan-walker-band");
      ctx.fillStyle = themedColor("--floor-plan-walker-band");
      ctx.lineWidth = BAND_SPRITE_SIZE * 0.4;

      // Head
      ctx.beginPath();
      ctx.arc(
        0,
        -BAND_SPRITE_SIZE * 3.5 + bob,
        BAND_SPRITE_SIZE * 0.7,
        0,
        Math.PI * 2,
      );
      ctx.fill();

      // Body
      ctx.lineWidth = BAND_SPRITE_SIZE * 0.35;
      ctx.beginPath();
      ctx.moveTo(0, -BAND_SPRITE_SIZE * 2.8 + bob);
      ctx.lineTo(0, -BAND_SPRITE_SIZE * 0.2 + bob);
      ctx.stroke();

      // Instrument-specific arms
      if (member.role === "guitar") {
        // Guitar strum
        ctx.beginPath();
        ctx.moveTo(-BAND_SPRITE_SIZE * 0.9, -BAND_SPRITE_SIZE * 2.2 + bob);
        ctx.lineTo(
          -BAND_SPRITE_SIZE * 0.5,
          -BAND_SPRITE_SIZE * 1.5 + bob + armSwing * BAND_SPRITE_SIZE * 0.3,
        );
        ctx.lineTo(-BAND_SPRITE_SIZE * 1.5, -BAND_SPRITE_SIZE * 1.8 + bob);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(BAND_SPRITE_SIZE * 0.9, -BAND_SPRITE_SIZE * 2.2 + bob);
        ctx.lineTo(
          BAND_SPRITE_SIZE * 0.5,
          -BAND_SPRITE_SIZE * 1.2 + bob - armSwing * BAND_SPRITE_SIZE * 0.5,
        );
        ctx.stroke();
        // Guitar body
        ctx.fillStyle = themedColor("--floor-plan-walker-instrument", 0.7);
        ctx.beginPath();
        ctx.ellipse(
          -BAND_SPRITE_SIZE * 1.8,
          -BAND_SPRITE_SIZE * 1.5 + bob,
          BAND_SPRITE_SIZE * 0.5,
          BAND_SPRITE_SIZE * 0.7,
          -0.3,
          0,
          Math.PI * 2,
        );
        ctx.fill();
      } else if (member.role === "drums") {
        // Drumsticks
        ctx.beginPath();
        ctx.moveTo(-BAND_SPRITE_SIZE * 0.5, -BAND_SPRITE_SIZE * 2 + bob);
        ctx.lineTo(
          -BAND_SPRITE_SIZE * 0.3,
          -BAND_SPRITE_SIZE * 3.5 + bob + armSwing * BAND_SPRITE_SIZE * 0.8,
        );
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(BAND_SPRITE_SIZE * 0.5, -BAND_SPRITE_SIZE * 2 + bob);
        ctx.lineTo(
          BAND_SPRITE_SIZE * 0.3,
          -BAND_SPRITE_SIZE * 3.5 + bob - armSwing * BAND_SPRITE_SIZE * 0.8,
        );
        ctx.stroke();
        // Snare drum
        ctx.fillStyle = themedColor("--floor-plan-walker-detail", 0.6);
        ctx.fillRect(
          -BAND_SPRITE_SIZE * 0.8,
          -BAND_SPRITE_SIZE * 1 + bob,
          BAND_SPRITE_SIZE * 1.6,
          BAND_SPRITE_SIZE * 0.5,
        );
      } else if (member.role === "keys") {
        // Keyboard player
        ctx.beginPath();
        ctx.moveTo(-BAND_SPRITE_SIZE * 0.8, -BAND_SPRITE_SIZE * 2.2 + bob);
        ctx.lineTo(-BAND_SPRITE_SIZE * 0.3, -BAND_SPRITE_SIZE * 1.2 + bob);
        ctx.lineTo(-BAND_SPRITE_SIZE * 1.2, -BAND_SPRITE_SIZE * 0.8 + bob);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(BAND_SPRITE_SIZE * 0.8, -BAND_SPRITE_SIZE * 2.2 + bob);
        ctx.lineTo(BAND_SPRITE_SIZE * 0.3, -BAND_SPRITE_SIZE * 1.2 + bob);
        ctx.lineTo(BAND_SPRITE_SIZE * 1.2, -BAND_SPRITE_SIZE * 0.8 + bob);
        ctx.stroke();
        // Keyboard
        ctx.fillStyle = themedColor("--floor-plan-walker-accent-alt", 0.6);
        ctx.fillRect(
          -BAND_SPRITE_SIZE * 1.5,
          -BAND_SPRITE_SIZE * 0.5 + bob,
          BAND_SPRITE_SIZE * 3,
          BAND_SPRITE_SIZE * 0.4,
        );
      } else if (member.role === "singer") {
        // Singer with mic
        ctx.beginPath();
        ctx.moveTo(-BAND_SPRITE_SIZE * 0.8, -BAND_SPRITE_SIZE * 2.2 + bob);
        ctx.lineTo(
          -BAND_SPRITE_SIZE * 0.2,
          -BAND_SPRITE_SIZE * 3.5 + bob + armSwing * BAND_SPRITE_SIZE * 0.2,
        );
        ctx.stroke();
        // Mic stand
        ctx.strokeStyle = themedColor("--floor-plan-walker-detail-dim", 0.5);
        ctx.lineWidth = BAND_SPRITE_SIZE * 0.1;
        ctx.beginPath();
        ctx.moveTo(-BAND_SPRITE_SIZE * 0.2, -BAND_SPRITE_SIZE * 3.5 + bob);
        ctx.lineTo(-BAND_SPRITE_SIZE * 0.2, -BAND_SPRITE_SIZE * 1.5 + bob);
        ctx.stroke();
        // Mic
        ctx.fillStyle = themedColor("--floor-plan-walker-shadow", 0.7);
        ctx.beginPath();
        ctx.arc(
          -BAND_SPRITE_SIZE * 0.2,
          -BAND_SPRITE_SIZE * 3.8 + bob + armSwing * BAND_SPRITE_SIZE * 0.2,
          BAND_SPRITE_SIZE * 0.2,
          0,
          Math.PI * 2,
        );
        ctx.fill();
        ctx.strokeStyle = themedColor("--floor-plan-walker-band");
        ctx.lineWidth = BAND_SPRITE_SIZE * 0.4;
      }

      // Legs
      const legSwing = Math.sin(member.armCycle) * 0.3;
      ctx.beginPath();
      ctx.moveTo(-BAND_SPRITE_SIZE * 0.4, -BAND_SPRITE_SIZE * 0.2 + bob);
      ctx.lineTo(
        -BAND_SPRITE_SIZE * 0.5 - legSwing * BAND_SPRITE_SIZE * 0.3,
        BAND_SPRITE_SIZE * 1.5,
      );
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(BAND_SPRITE_SIZE * 0.4, -BAND_SPRITE_SIZE * 0.2 + bob);
      ctx.lineTo(
        BAND_SPRITE_SIZE * 0.5 + legSwing * BAND_SPRITE_SIZE * 0.3,
        BAND_SPRITE_SIZE * 1.5,
      );
      ctx.stroke();

      // Role label
      ctx.fillStyle = themedColor("--floor-plan-walker-ink", 0.5);
      ctx.font = "8px sans-serif";
      ctx.textAlign = "center";
      const labels: Record<string, string> = {
        guitar: "🎸",
        drums: "🥁",
        keys: "🎹",
        singer: "🎤",
      };
      ctx.fillText(labels[member.role], 0, BAND_SPRITE_SIZE * 2.5);

      ctx.restore();
    };

    // ── Zombie Drawing (Iteration 4) ──

    const drawZombie = (ctx: CanvasRenderingContext2D, zombie: Zombie) => {
      ctx.save();
      ctx.translate(zombie.x, zombie.y);

      const wobbleX = Math.sin(zombie.wobble * 0.8) * 2;
      const wobbleRot = Math.sin(zombie.wobble * 0.6) * 0.08;
      ctx.translate(wobbleX, 0);
      ctx.rotate(wobbleRot);
      ctx.scale(zombie.facing, 1);

      // Zombie shade (green-gray)
      const shade = zombie.shade;
      const low = themedColor("--floor-plan-walker-zombie-low")
        .split(",")
        .map(Number);
      const high = themedColor("--floor-plan-walker-zombie-high")
        .split(",")
        .map(Number);
      const r = Math.floor(low[0] + shade * (high[0] - low[0]));
      const g = Math.floor(low[1] + shade * (high[1] - low[1]));
      const b = Math.floor(low[2] + shade * (high[2] - low[2]));
      const zombieCol = `rgba(${r},${g},${b},0.85)`;
      const zombieColDim = `rgba(${r},${g},${b},0.5)`;

      ctx.strokeStyle = zombieCol;
      ctx.fillStyle = zombieCol;
      ctx.lineWidth = S * 0.4;

      // Shambling leg cycle
      const leg = Math.sin(zombie.legCycle * 0.5) * 0.4;

      // Head - tilted, droopy
      ctx.beginPath();
      ctx.arc(S * 0.2, -S * 3.9, S * 0.85, 0, Math.PI * 2);
      ctx.fill();
      // X eyes (dead)
      ctx.strokeStyle = themedColor("--floor-plan-walker-shadow", 0.5);
      ctx.lineWidth = S * 0.15;
      ctx.beginPath();
      ctx.moveTo(-S * 0.35, -S * 4.1);
      ctx.lineTo(-S * 0.05, -S * 3.8);
      ctx.moveTo(-S * 0.05, -S * 4.1);
      ctx.lineTo(-S * 0.35, -S * 3.8);
      ctx.moveTo(S * 0.05, -S * 4.1);
      ctx.lineTo(S * 0.35, -S * 3.8);
      ctx.moveTo(S * 0.35, -S * 4.1);
      ctx.lineTo(S * 0.05, -S * 3.8);
      ctx.stroke();

      // Body - slumped
      ctx.strokeStyle = zombieCol;
      ctx.lineWidth = S * 0.45;
      ctx.beginPath();
      ctx.moveTo(0, -S * 3.0);
      ctx.lineTo(S * 0.1, S * 0.3);
      ctx.stroke();

      // Arms - outstretched forward, zombie style
      ctx.lineWidth = S * 0.3;
      const armRaise = Math.sin(zombie.wobble * 0.4) * 0.2;
      // Left arm
      ctx.beginPath();
      ctx.moveTo(-S * 1.1, -S * 2.3);
      ctx.lineTo(-S * 2.2, -S * 1.8 + armRaise * S);
      ctx.lineTo(-S * 2.8, -S * 2.2 + armRaise * S);
      ctx.stroke();
      // Right arm
      ctx.beginPath();
      ctx.moveTo(S * 1.1, -S * 2.3);
      ctx.lineTo(S * 2.0, -S * 1.6 + armRaise * S);
      ctx.lineTo(S * 2.5, -S * 2.0 + armRaise * S);
      ctx.stroke();

      // Legs - shuffling
      ctx.lineWidth = S * 0.32;
      ctx.beginPath();
      ctx.moveTo(-S * 0.5, S * 0.3);
      ctx.lineTo(-S * 0.7 - leg * S * 0.4, S * 1.6);
      ctx.lineTo(-S * 0.5 - leg * S * 0.5, S * 2.6);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(S * 0.5, S * 0.3);
      ctx.lineTo(S * 0.7 + leg * S * 0.4, S * 1.6);
      ctx.lineTo(S * 0.5 + leg * S * 0.5, S * 2.6);
      ctx.stroke();

      // Drippy details
      ctx.fillStyle = `rgba(${r},${g - 20},${b},0.4)`;
      ctx.beginPath();
      ctx.arc(-S * 0.3, -S * 3.5, S * 0.12, 0, Math.PI * 2);
      ctx.fill();

      ctx.restore();
    };

    // ── New Iteration 4 Activities ──

    const drawFacepalm = (ctx: CanvasRenderingContext2D, c: number) => {
      // Phased: 0-25 raise hand to face, 25-50 palm to face (embarrassment), 50-75 lower, repeat
      const phase = c % 75;
      let armAngle = 0;
      let palmY = -S * 2.4;
      let headTilt = 0;

      if (phase < 25) {
        const t = easeOutQuint(phase / 25);
        armAngle = t * (-Math.PI * 0.3);
        palmY = -S * 2.4 + t * (-S * 1.5);
        headTilt = t * 0.1;
      } else if (phase < 50) {
        const t = (phase - 25) / 25;
        armAngle = -Math.PI * 0.3;
        palmY = -S * 3.9;
        headTilt = 0.1 + Math.sin(t * Math.PI) * 0.05;
      } else {
        const t = (phase - 50) / 25;
        armAngle = -Math.PI * 0.3 * (1 - easeOutQuint(t));
        palmY = -S * 3.9 + easeOutQuint(t) * S * 1.5;
        headTilt = 0.1 * (1 - easeOutQuint(t));
      }

      ctx.save();
      ctx.rotate(headTilt);
      drawHat(ctx, 0);
      ctx.rotate(-headTilt * 0.5);
      drawHead(ctx, 0);
      ctx.restore();

      drawTorso(ctx, 0);

      // Facepalm arm
      ctx.strokeStyle = COL;
      ctx.lineWidth = S * 0.32;
      const handX = -S * 0.3 + Math.sin(armAngle) * S * 0.5;
      ctx.beginPath();
      ctx.moveTo(-S * 1.2, -S * 2.4);
      ctx.lineTo(-S * 0.5, -S * 3.0);
      ctx.lineTo(handX, palmY);
      ctx.stroke();

      // Open palm on face
      ctx.lineWidth = S * 0.15;
      ctx.beginPath();
      ctx.moveTo(handX - S * 0.3, palmY);
      ctx.lineTo(handX + S * 0.3, palmY);
      ctx.lineTo(handX + S * 0.2, palmY + S * 0.4);
      ctx.lineTo(handX - S * 0.2, palmY + S * 0.4);
      ctx.closePath();
      ctx.stroke();

      // Other arm hanging
      ctx.lineWidth = S * 0.32;
      ctx.beginPath();
      ctx.moveTo(S * 1.2, -S * 2.4);
      ctx.lineTo(S * 1.5, -S * 1.0);
      ctx.lineTo(S * 1.0, -S * 0.2);
      ctx.stroke();

      drawLegs(ctx, 0, 0, 0.1);
    };

    const drawHappyDance = (ctx: CanvasRenderingContext2D, c: number) => {
      // Energetic celebration dance with arm swings and footwork
      const beat = c % 20;
      const fullCycle = Math.sin(beat * 0.3) * 0.5;
      const bounce = Math.abs(Math.sin(beat * 0.15)) * S * 1.5;

      ctx.save();
      ctx.translate(0, -bounce);

      drawHat(ctx, 0);
      ctx.save();
      ctx.rotate(Math.sin(c * 0.1) * 0.05);
      drawHead(ctx, 0);
      ctx.restore();
      drawTorso(ctx, 0);

      // Arms swinging opposite to each other
      ctx.strokeStyle = COL;
      ctx.lineWidth = S * 0.32;
      const armL = Math.sin(c * 0.12) * 1.2;
      const armR = -Math.sin(c * 0.12) * 1.2;

      ctx.beginPath();
      ctx.moveTo(-S * 1.2, -S * 2.4);
      ctx.lineTo(
        -S * 1.5 - Math.sin(armL) * S * 1.5,
        -S * 2.0 - Math.cos(armL) * S * 1.5,
      );
      ctx.stroke();

      ctx.beginPath();
      ctx.moveTo(S * 1.2, -S * 2.4);
      ctx.lineTo(
        S * 1.5 - Math.sin(armR) * S * 1.5,
        -S * 2.0 - Math.cos(armR) * S * 1.5,
      );
      ctx.stroke();

      // Happy footwork
      const legSwing = Math.sin(c * 0.15) * 0.6;
      drawLegs(ctx, 0, legSwing, 0.2);

      ctx.restore();

      // Sparkle particles
      if (beat > 15 && c % 5 === 0) {
        spawnSparkle(
          w.x + (Math.random() - 0.5) * S * 4,
          w.y - S * 3 + (Math.random() - 0.5) * S * 2,
          themedColor("--floor-plan-walker-accent"),
        );
      }
    };

    const drawLookBothWays = (ctx: CanvasRenderingContext2D, c: number) => {
      // Suspicious looking left then right
      const phase = c % 80;
      let headTurn = 0;

      if (phase < 30) {
        const t = phase / 30;
        headTurn = easeInOutBack(t) * -0.4; // look left
      } else if (phase < 40) {
        headTurn = -0.4;
      } else if (phase < 55) {
        const t = (phase - 40) / 15;
        headTurn = -0.4 + easeInOutBack(t) * 0.8; // look right
      } else if (phase < 65) {
        headTurn = 0.4;
      } else {
        const t = (phase - 65) / 15;
        headTurn = 0.4 * (1 - easeOutQuint(t));
      }

      ctx.rotate(0); // body stays still
      drawHat(ctx, 0);

      ctx.save();
      ctx.rotate(headTurn);
      drawHead(ctx, 0);
      ctx.restore();

      drawTorso(ctx, 0);

      // Arms at sides, watchful
      ctx.strokeStyle = COL;
      ctx.lineWidth = S * 0.32;
      ctx.beginPath();
      ctx.moveTo(-S * 1.2, -S * 2.4);
      ctx.lineTo(-S * 1.4, -S * 1.2);
      ctx.lineTo(-S * 1.0, -S * 0.2);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(S * 1.2, -S * 2.4);
      ctx.lineTo(S * 1.4, -S * 1.2);
      ctx.lineTo(S * 1.0, -S * 0.2);
      ctx.stroke();

      drawLegs(ctx, 0, 0, 0.1);
    };

    const drawSprint = (ctx: CanvasRenderingContext2D, c: number) => {
      // Brief running burst - very fast leg and arm movement
      const speed = 0.25; // faster than normal walk
      const cycle = c * speed;
      const arm = Math.sin(cycle) * 0.8;
      const leg = Math.sin(cycle) * 0.9;
      const bounce = Math.abs(Math.sin(cycle * 2)) * S * 1.2;

      ctx.save();
      ctx.translate(0, -bounce);

      // Leaning forward (running pose)
      ctx.rotate(0.15);

      drawHat(ctx, 0);
      ctx.save();
      ctx.rotate(-0.1); // head counter-lean
      drawHead(ctx, 0);
      ctx.restore();
      drawTorso(ctx, 0);

      // Arms pumping
      ctx.strokeStyle = COL;
      ctx.lineWidth = S * 0.32;
      ctx.beginPath();
      ctx.moveTo(-S * 1.2, -S * 2.4);
      ctx.lineTo(-S * 1.6 - arm * S * 1.2, -S * 1.8);
      ctx.lineTo(-S * 1.3 - arm * S * 0.8, -S * 0.8);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(S * 1.2, -S * 2.4);
      ctx.lineTo(S * 1.6 + arm * S * 1.2, -S * 1.8);
      ctx.lineTo(S * 1.3 + arm * S * 0.8, -S * 0.8);
      ctx.stroke();

      // Running legs
      ctx.beginPath();
      ctx.moveTo(-S * 0.5, S * 0.3);
      ctx.lineTo(-S * 0.8 - leg * S * 0.8, S * 1.4);
      ctx.lineTo(-S * 0.3 - leg * S * 0.6, S * 2.6);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(S * 0.5, S * 0.3);
      ctx.lineTo(S * 0.8 + leg * S * 0.8, S * 1.4);
      ctx.lineTo(S * 0.3 + leg * S * 0.6, S * 2.6);
      ctx.stroke();

      ctx.restore();

      // Motion blur lines
      ctx.strokeStyle = themedColor("--floor-plan-walker-ink", 0.15);
      ctx.lineWidth = S * 0.1;
      for (let i = 1; i <= 3; i++) {
        ctx.beginPath();
        ctx.moveTo(-S * i * 1.5, -S * 2);
        ctx.lineTo(-S * i * 1.5, S * 0.5);
        ctx.stroke();
      }
    };

    // ── Main person renderer ──

    const drawPerson = (
      ctx: CanvasRenderingContext2D,
      x: number,
      y: number,
      legCycle: number,
      facing: number,
      fallAngle: number,
      getUpProg: number,
      portalScale: number,
      activity: Activity | null,
      actCycle: number,
      scaleX = 1,
      scaleY = 1,
      idleWobble = 0,
      fidgetAction = "",
    ) => {
      ctx.save();
      ctx.translate(x, y);
      if (portalScale < 1) ctx.scale(portalScale, portalScale);

      // Fall physics — pivot at feet
      if (fallAngle !== 0) {
        ctx.translate(0, S * 3.1);
        ctx.rotate(fallAngle);
        ctx.translate(0, -S * 3.1);
      }
      if (getUpProg > 0 && getUpProg < 1) {
        ctx.translate(0, S * 3.1);
        ctx.rotate((Math.PI / 2) * w.facing * (1 - getUpProg));
        ctx.translate(0, -S * 3.1);
      }

      // Squash/stretch + facing
      ctx.scale(facing * scaleX, scaleY);

      // Alpha flicker for sketchy hand-drawn feel (Iteration 5)
      const alphaFlicker = 1 + Math.sin(w.lineWobblePhase * 3.7) * 0.015;
      ctx.globalAlpha *= alphaFlicker;

      // Idle fidget (Iteration 5)
      if (!activity && fidgetAction) {
        if (fidgetAction === "head_tilt") {
          ctx.rotate(idleWobble * 3);
        } else if (fidgetAction === "shoulder_shrug") {
          ctx.translate(0, idleWobble * S * 0.5);
        }
      }

      if (activity) {
        switch (activity) {
          case "sipping":
            drawSipping(ctx, actCycle);
            break;
          case "jumping_jacks":
            drawJumpingJacks(ctx, actCycle);
            break;
          case "stretching":
            drawStretching(ctx, actCycle);
            break;
          case "pushups":
            drawPushups(ctx, actCycle);
            break;
          case "phone":
            drawPhone(ctx, actCycle);
            break;
          case "selfie":
            drawSelfie(ctx, actCycle);
            break;
          case "picnic":
            drawPicnic(ctx, actCycle);
            break;
          case "sitting":
            drawSitting(ctx, actCycle);
            break;
          case "chef_kiss":
            drawChefKiss(ctx, actCycle);
            break;
          case "scratch_head":
            drawScratchHead(ctx, actCycle);
            break;
          case "point":
            drawPoint(ctx, actCycle);
            break;
          case "shrug":
            drawShrug(ctx, actCycle);
            break;
          case "facepalm":
            drawFacepalm(ctx, actCycle);
            break;
          case "happy_dance":
            drawHappyDance(ctx, actCycle);
            break;
          case "look_both_ways":
            drawLookBothWays(ctx, actCycle);
            break;
          case "sprint":
            drawSprint(ctx, actCycle);
            break;
        }
      } else {
        drawWalking(ctx, legCycle);
      }

      ctx.restore();
    };

    // ── Activity emojis ──
    const activityEmoji: Record<Activity, string> = {
      sipping: "🍷",
      stretching: "🧘",
      pushups: "💪",
      phone: "📱",
      selfie: "🤳",
      picnic: "🧺",
      jumping_jacks: "⭐",
      sitting: "📰",
      chef_kiss: "😘",
      scratch_head: "🤔",
      point: "👈",
      shrug: "🤷",
      facepalm: "🤦",
      happy_dance: "💃",
      look_both_ways: "👀",
      sprint: "🏃",
    };

    // ── Main loop ──

    const animate = () => {
      const canvas = canvasRef.value;
      const grid = getGrid();
      if (!canvas || !grid) {
        animHandle = requestAnimationFrame(animate);
        return;
      }

      const rect = grid.getBoundingClientRect();
      const cw = Math.floor(rect.width);
      const ch = Math.floor(rect.height);
      if (canvas.width !== cw || canvas.height !== ch) {
        canvas.width = cw;
        canvas.height = ch;
      }

      if (!w.initialized && cw > 0 && ch > 0) {
        const obs = getObstacles();
        const safe = findSafeSpot(cw, ch, obs);
        w.x = safe.x;
        w.y = safe.y;
        w.lastX = w.x;
        w.lastY = w.y;
        w.initialized = true;
        pickSafeTarget(cw, ch, obs);
      }

      const ctx = canvas.getContext("2d")!;
      ctx.clearRect(0, 0, cw, ch);
      if (!w.initialized) {
        animHandle = requestAnimationFrame(animate);
        return;
      }

      const obstacles = getObstacles();
      let portalScale = 1;
      let currentActivity: Activity | null = null;

      switch (w.phase) {
        case "falling": {
          w.phaseTimer--;
          const prog = 1 - w.phaseTimer / 22;
          w.fallAngle = (Math.PI / 2) * w.facing * prog * prog;
          w.x += w.velocityX * 0.05;
          w.y += w.velocityY * 0.05;
          if (w.phaseTimer <= 0) {
            w.phase = "vertigo";
            w.phaseTimer = 55;
            w.vertigoAngle = 0;
            w.fallAngle = (Math.PI / 2) * w.facing;
            w.bubble = "💫";
            w.bubbleTimer = 55;
          }
          break;
        }
        case "vertigo": {
          w.phaseTimer--;
          w.vertigoAngle += 0.09;
          w.fallAngle =
            (Math.PI / 2) * w.facing + Math.sin(w.vertigoAngle * 3) * 0.1;
          if (w.phaseTimer <= 0) {
            w.phase = "gettingUp";
            w.phaseTimer = 40;
            w.getUpProgress = 0;
          }
          break;
        }
        case "gettingUp": {
          w.phaseTimer--;
          w.getUpProgress = 1 - w.phaseTimer / 40;
          w.fallAngle = 0;
          if (w.phaseTimer <= 0) {
            w.phase = "stopped";
            w.phaseTimer = 20 + Math.random() * 15;
            w.getUpProgress = 0;
            w.stuckCounter = 0;
            w.bubble = "😵";
            w.bubbleTimer = 30;
            // After getting up, adjust heading away from obstacle but keep quest target
            const away = Math.atan2(w.velocityY, w.velocityX) + Math.PI;
            w.heading = away;
          }
          break;
        }
        case "stopped": {
          w.phaseTimer--;
          if (w.phaseTimer <= 0) w.phase = "walking";
          break;
        }
        case "enterPortal": {
          w.phaseTimer--;
          portalScale = Math.max(0, w.phaseTimer / 25);
          w.fallAngle += 0.35;
          w.y += 0.3;
          if (w.phaseTimer <= 0) {
            // Teleport near the quest target if active, else random safe spot
            const dest = quest.active
              ? findSafeSpot(cw, ch, obstacles, { x: w.x, y: w.y })
              : findSafeSpot(cw, ch, obstacles, { x: w.x, y: w.y });
            portals.push({
              x: dest.x,
              y: dest.y + 8,
              radius: 10,
              phase: "opening",
              timer: 0,
              isExit: true,
            });
            w.x = dest.x;
            w.y = dest.y;
            w.phase = "exitPortal";
            w.phaseTimer = 20;
            w.fallAngle = 0;
            w.stuckCounter = 0;
          }
          break;
        }
        case "exitPortal": {
          w.phaseTimer--;
          portalScale = Math.min(1, 1 - w.phaseTimer / 20);
          w.y -= 0.4;
          if (w.phaseTimer <= 0) {
            w.phase = "walking";
            w.bubble = "✨";
            w.bubbleTimer = 30;
          }
          break;
        }
        case "stopping": {
          // Gradual slow-down: keep walking but decelerate
          w.phaseTimer--;
          const slowFactor = w.phaseTimer / 30; // 1→0
          w.legCycle += 0.08 * slowFactor;
          w.x += w.velocityX * slowFactor * 0.5;
          w.y += w.velocityY * slowFactor * 0.5;

          // Squash on stop (Iteration 3)
          if (w.phaseTimer === 15) {
            w.targetScaleY = 0.85;
            w.targetScaleX = 1.15;
          }

          // Anticipation for high-energy activities (Iteration 3)
          const highEnergy: Activity[] = ["jumping_jacks"];
          if (w.phaseTimer === 20 && highEnergy.includes(w.currentActivity)) {
            w.isAnticipating = true;
            w.anticipationTimer = ANTICIPATION_FRAMES;
          }

          if (w.phaseTimer <= 0) {
            w.phase = "activity";
            w.phaseTimer = 400 + Math.random() * 300; // long activity ~7-12 seconds
            w.activityCycle = 0;
            w.bubble = activityEmoji[w.currentActivity];
            w.bubbleTimer = 60;
            // Set eye openness based on activity (Iteration 3)
            if (
              w.currentActivity === "phone" ||
              w.currentActivity === "sitting"
            ) {
              w.eyeOpenness = 0.6; // bored, half-lidded
            } else if (w.currentActivity === "chef_kiss") {
              w.eyeOpenness = 0.4; // excited squint
            } else {
              w.eyeOpenness = 1.0;
            }
          }
          break;
        }
        case "activity": {
          w.phaseTimer--;
          w.activityCycle++;

          // Squash/stretch at activity start (Iteration 3)
          if (w.activityCycle === 1) {
            w.targetScaleY = 1.1;
            w.targetScaleX = 0.9;
            setTimeout(() => {
              w.targetScaleY = 0.95;
              w.targetScaleX = 1.05;
            }, 100);
          }

          currentActivity = w.currentActivity;

          // Eye openness fluctuation during activities (Iteration 3)
          if (w.activityCycle % 60 === 0) {
            if (
              w.currentActivity === "sipping" ||
              w.currentActivity === "picnic"
            ) {
              w.eyeOpenness = 0.5 + Math.random() * 0.5; // relaxed, drowsy
            } else if (
              w.currentActivity === "phone" ||
              w.currentActivity === "sitting" ||
              w.currentActivity === "chef_kiss"
            ) {
              w.eyeOpenness = 0.4 + Math.random() * 0.3; // varied
            } else {
              w.eyeOpenness = 0.7 + Math.random() * 0.3; // alert
            }
          }

          if (w.phaseTimer <= 0) {
            w.phase = "stopped";
            w.phaseTimer = 30; // brief pause, then new quest spawns
            w.eyeOpenness = 1.0; // reset eyes
            // New quest will be spawned when walking phase starts and quest.active is false
          }
          break;
        }
        case "bandStory": {
          // Walker arrives at band spot, band members appear and play
          bandStoryTimer++;

          if (bandStoryPhase === "approaching") {
            // Walk toward the band spot
            const bdx = quest.x - w.x;
            const bdy = quest.y - w.y;
            const bdist = Math.sqrt(bdx * bdx + bdy * bdy);
            if (bdist > 30) {
              w.x += (bdx / bdist) * w.speed * 1.5;
              w.y += (bdy / bdist) * w.speed * 1.5;
              if (bdx !== 0) w.facing = bdx > 0 ? 1 : -1;
              w.legCycle += 0.12;
            } else {
              // Arrived - spawn band members
              bandStoryPhase = "performing";
              bandStoryTimer = 0;
              const roles: Array<"guitar" | "drums" | "keys" | "singer"> = [
                "guitar",
                "drums",
                "keys",
                "singer",
              ];
              bandMembers.length = 0;
              for (let i = 0; i < 4; i++) {
                const angle = (i / 4) * Math.PI * 2;
                bandMembers.push({
                  x: quest.x + Math.cos(angle) * 25,
                  y: quest.y + Math.sin(angle) * 25,
                  role: roles[i],
                  armCycle: Math.random() * Math.PI * 2,
                  bobCycle: Math.random() * Math.PI * 2,
                  facing: 1,
                });
              }
              w.bubble = "🎸";
              w.bubbleTimer = 60;
            }
          } else if (bandStoryPhase === "performing") {
            // Animate band members
            for (const member of bandMembers) {
              member.armCycle += 0.15;
              member.bobCycle += 0.08;
            }
            w.activityCycle++;
            currentActivity = "happy_dance";

            // Musical notes particles
            if (bandStoryTimer % 15 === 0) {
              spawnSparkle(
                quest.x + (Math.random() - 0.5) * 30,
                quest.y - 20 + (Math.random() - 0.5) * 20,
                themedColor("--floor-plan-walker-warm"),
              );
              spawnSparkle(
                quest.x + (Math.random() - 0.5) * 30,
                quest.y - 20 + (Math.random() - 0.5) * 20,
                themedColor("--floor-plan-walker-accent-alt"),
              );
            }

            if (bandStoryTimer > 300) {
              // ~5 seconds of performing
              bandStoryPhase = "leaving";
              bandStoryTimer = 0;
            }
          } else if (bandStoryPhase === "leaving") {
            // Walker leaves, band fades
            const ldx = w.targetX - w.x;
            const ldy = w.targetY - w.y;
            const ldist = Math.sqrt(ldx * ldx + ldy * ldy);
            if (ldist > 50) {
              w.x += (ldx / ldist) * w.speed * 1.5;
              w.y += (ldy / ldist) * w.speed * 1.5;
              if (ldx !== 0) w.facing = ldx > 0 ? 1 : -1;
              w.legCycle += 0.12;
            }
            if (bandStoryTimer > 120) {
              bandMembers.length = 0;
              bandStoryPhase = "approaching";
              w.phase = "stopped";
              w.phaseTimer = 30;
              w.bubble = "🎶";
              w.bubbleTimer = 40;
            }
          }
          break;
        }
        case "zombieHorde": {
          // Walker turns into zombie and gathers a horde
          bandStoryTimer++;

          if (bandStoryPhase === "approaching") {
            // Initial zombie transformation
            if (bandStoryTimer === 30) {
              w.bubble = "🧟";
              w.bubbleTimer = 60;
              w.targetScaleY = 0.9;
              w.targetScaleX = 1.1;
            }

            // Spawn zombie horde
            if (bandStoryTimer === 60 && zombies.length === 0) {
              const zombieCount = 3 + Math.floor(Math.random() * 3); // 3-5 zombies
              for (let i = 0; i < zombieCount; i++) {
                const angle = Math.random() * Math.PI * 2;
                const dist = 40 + Math.random() * 30;
                zombies.push({
                  x: w.x + Math.cos(angle) * dist,
                  y: w.y + Math.sin(angle) * dist,
                  vx: 0,
                  vy: 0,
                  legCycle: Math.random() * 100,
                  facing: Math.random() > 0.5 ? 1 : -1,
                  wobble: Math.random() * 100,
                  shade: Math.random(),
                });
              }
              bandStoryPhase = "performing";
              bandStoryTimer = 0;
            }
          } else if (bandStoryPhase === "performing") {
            // Horde shambles toward target
            w.activityCycle++;
            currentActivity = "sprint"; // fast shamble

            const zdx = w.targetX - w.x;
            const zdy = w.targetY - w.y;
            const zdist = Math.sqrt(zdx * zdx + zdy * zdy);

            if (zdist > quest.reachDistance) {
              const zspeed = 0.06;
              w.x += (zdx / zdist) * zspeed;
              w.y += (zdy / zdist) * zspeed;
              w.legCycle += 0.06;

              // Zombies follow walker
              for (const zombie of zombies) {
                zombie.wobble += 0.1;
                zombie.legCycle += 0.05;
                const zldx = w.x - zombie.x;
                const zldy = w.y - zombie.y;
                const zldist = Math.sqrt(zldx * zldx + zldy * zldy);
                if (zldist > 30) {
                  zombie.x += (zldx / zldist) * 0.04;
                  zombie.y += (zldy / zldist) * 0.04;
                }
                zombie.facing = zldx > 0 ? 1 : -1;
              }
            } else {
              // Reached target
              bandStoryPhase = "leaving";
              bandStoryTimer = 0;
            }

            if (bandStoryTimer > 480) {
              // timeout after ~8 seconds
              bandStoryPhase = "leaving";
              bandStoryTimer = 0;
            }
          } else if (bandStoryPhase === "leaving") {
            // Zombies disperse
            for (const zombie of zombies) {
              zombie.wobble += 0.15;
              zombie.x += (Math.random() - 0.5) * 0.5;
              zombie.y += (Math.random() - 0.5) * 0.5;
            }
            w.legCycle += 0.08;

            if (bandStoryTimer > 120) {
              zombies.length = 0;
              bandStoryPhase = "approaching";
              w.phase = "stopped";
              w.phaseTimer = 30;
              w.bubble = "💀";
              w.bubbleTimer = 40;
              w.targetScaleY = 1.0;
              w.targetScaleX = 1.0;
            }
          }
          break;
        }
        default: {
          // ── Quest-based walking ──
          // Spawn quest target if none active
          if (!quest.active) {
            spawnQuest(cw, ch, obstacles);
          }

          // Navigate toward quest target
          const dx = w.targetX - w.x;
          const dy = w.targetY - w.y;
          const dist = Math.sqrt(dx * dx + dy * dy);

          // Reached the quest target?
          if (dist < quest.reachDistance && quest.active) {
            quest.active = false;
            // Check for special story quests
            if (quest.icon.emoji === "🎸") {
              // Band story - walker heads to band spot, band appears
              bandStoryPhase = "approaching";
              bandStoryTimer = 0;
              w.phase = "bandStory";
              w.phaseTimer = 0;
              w.activityCycle = 0;
              w.targetX = quest.x + 50; // offset so walker doesn't overlap band
              w.targetY = quest.y;
              break;
            } else if (quest.icon.emoji === "🧟") {
              // Zombie horde story
              bandStoryPhase = "approaching";
              bandStoryTimer = 0;
              w.phase = "zombieHorde";
              w.phaseTimer = 0;
              w.activityCycle = 0;
              break;
            }
            w.phase = "stopping";
            w.currentActivity = quest.icon.activity;
            w.phaseTimer = 30;
            w.activityCycle = 0;
            break;
          }

          // Quest timeout — can't reach it after ~60 seconds, give up
          if (quest.active) {
            quest.age++;
            if (quest.age > quest.maxAge) {
              quest.active = false; // will spawn new quest next frame
              w.bubble = "🤷";
              w.bubbleTimer = 40;
              w.stuckCounter = 0;
            }
          }

          // Steer toward target — smooth turning
          const desired = Math.atan2(dy, dx);
          let diff = desired - w.heading;
          while (diff > Math.PI) diff -= Math.PI * 2;
          while (diff < -Math.PI) diff += Math.PI * 2;
          w.heading += diff * 0.035; // responsive steering toward goal

          // Gentle drift for organic feel (subtle, not random)
          const t = Date.now() * 0.001;
          const drift = Math.sin(t * 0.5) * 0.03 + Math.sin(t * 1.1 + 3) * 0.02;

          // Momentum-based acceleration (Iteration 3)
          const targetVX = Math.cos(w.heading + drift) * w.speed;
          const targetVY = Math.sin(w.heading + drift) * w.speed;
          w.velocityX =
            w.velocityX * MOMENTUM_FRICTION +
            targetVX * (1 - MOMENTUM_FRICTION);
          w.velocityY =
            w.velocityY * MOMENTUM_FRICTION +
            targetVY * (1 - MOMENTUM_FRICTION);

          let nx = w.x + w.velocityX;
          let ny = w.y + w.velocityY;

          const hit = hitTest(nx, ny, obstacles, 10);
          if (hit) {
            w.stuckCounter++;
            if (w.stuckCounter > 150) {
              // Completely stuck — portal to quest target vicinity
              w.phase = "enterPortal";
              w.phaseTimer = 25;
              w.fallAngle = 0;
              portals.push({
                x: w.x,
                y: w.y + 8,
                radius: 10,
                phase: "opening",
                timer: 0,
                isExit: false,
              });
              w.bubble = "🕳️";
              w.bubbleTimer = 25;
            } else if (w.stuckCounter > 20 && !wasNearFall(w.x, w.y)) {
              // Bump and fall
              w.fallSpots.push({ x: w.x, y: w.y });
              if (w.fallSpots.length > 8) w.fallSpots.shift();
              w.phase = "falling";
              w.phaseTimer = 22;
              w.fallAngle = 0;
              w.bubble = "💥";
              w.bubbleTimer = 22;
              const ox = w.x - (hit.left + hit.width / 2);
              const oy = w.y - (hit.top + hit.height / 2);
              const od = Math.sqrt(ox * ox + oy * oy) || 1;
              w.velocityX = (ox / od) * w.speed * 2;
              w.velocityY = (oy / od) * w.speed * 2;
            } else {
              // Steer around obstacle — find best corner to navigate to
              const corners = [
                { x: hit.left - 14, y: hit.top - 14 },
                { x: hit.left + hit.width + 14, y: hit.top - 14 },
                { x: hit.left - 14, y: hit.top + hit.height + 14 },
                { x: hit.left + hit.width + 14, y: hit.top + hit.height + 14 },
              ];
              // Pick corner that's closest to the quest target (not just ahead)
              let best = corners[0],
                bestScore = Infinity;
              for (const c of corners) {
                if (hitTest(c.x, c.y, obstacles, 8)) continue;
                // Score: distance from corner to quest target (prefer shorter path)
                const toTarget = Math.sqrt(
                  (c.x - w.targetX) ** 2 + (c.y - w.targetY) ** 2,
                );
                const toCorner = Math.sqrt((c.x - w.x) ** 2 + (c.y - w.y) ** 2);
                const score = toTarget + toCorner * 0.3;
                if (score < bestScore) {
                  bestScore = score;
                  best = c;
                }
              }
              const ca = Math.atan2(best.y - w.y, best.x - w.x);
              w.heading = ca;
              const sx = w.x + Math.cos(ca) * w.speed;
              const sy = w.y + Math.sin(ca) * w.speed;
              if (!hitTest(sx, sy, obstacles, 8)) {
                w.x = sx;
                w.y = sy;
              } else w.stuckCounter += 3;
            }
          } else {
            w.x = nx;
            w.y = ny;
            if (w.stuckCounter > 0)
              w.stuckCounter = Math.max(0, w.stuckCounter - 2);
          }

          // Keep in bounds (no edge wrapping with quest system)
          w.x = Math.max(5, Math.min(cw - 5, w.x));
          w.y = Math.max(10, Math.min(ch - 5, w.y));

          if (Math.abs(w.velocityX) > 0.02) {
            // Detect direction change for squash (Iteration 3)
            const newFacing = w.velocityX > 0 ? 1 : -1;
            if (newFacing !== w.facing) {
              w.targetScaleY = 0.85;
              w.targetScaleX = 1.2;
            }
            w.facing = newFacing;
          }

          // Walking bob squash/stretch (Iteration 3)
          const bobPhase = Math.sin(w.legCycle * 2);
          if (bobPhase > 0.9) {
            w.targetScaleY = 1 + (bobPhase - 0.9) * 0.5;
            w.targetScaleX = 1 - (bobPhase - 0.9) * 0.3;
          }

          w.legCycle += 0.08;

          // Footprints with occasional message (Iteration 5)
          const trav = Math.sqrt((w.x - w.lastX) ** 2 + (w.y - w.lastY) ** 2);
          w.stepAccum += trav;
          if (w.stepAccum > 14) {
            w.stepAccum = 0;
            const side = Math.sin(w.legCycle) > 0 ? 1 : -1;
            // Random footprint messages
            let msg: string | undefined;
            if (Math.random() < 0.08) {
              const msgs = [
                "yay!",
                "nice",
                "wow!",
                "keeps going",
                "yummy",
                "refreshing",
                "delicious",
              ];
              msg = msgs[Math.floor(Math.random() * msgs.length)];
            }
            footprints.push({
              x: w.x + side * w.facing * 1.5,
              y: w.y + 8,
              age: 0,
              facing: w.facing,
              message: msg,
            });
            if (footprints.length > MAX_PRINTS) footprints.shift();
          }
          w.lastX = w.x;
          w.lastY = w.y;
          break;
        }
      }

      // ── Update portals ──
      for (let i = portals.length - 1; i >= 0; i--) {
        portals[i].timer++;
        if (portals[i].phase === "opening" && portals[i].timer >= 20) {
          portals[i].phase = "closing";
          portals[i].timer = 0;
        }
        if (portals[i].phase === "closing" && portals[i].timer >= 15)
          portals.splice(i, 1);
      }

      // ── Update particles (Iteration 3) ──
      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i];
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.02; // slight gravity
        p.life--;
        if (p.life <= 0) particles.splice(i, 1);
      }

      // ── Update trail points (Iteration 3) ──
      if (quest.active && w.phase === "walking") {
        if (Math.random() < 0.3) {
          trailPoints.push({ x: quest.x, y: quest.y, age: 0 });
          if (trailPoints.length > PARTICLE_TRAIL_COUNT) trailPoints.shift();
        }
      }
      for (let i = trailPoints.length - 1; i >= 0; i--) {
        trailPoints[i].age++;
        if (trailPoints[i].age > 40) trailPoints.splice(i, 1);
      }

      // ── Update squash/stretch physics (Iteration 3) ──
      w.scaleX += (w.targetScaleX - w.scaleX) * 0.15;
      w.scaleY += (w.targetScaleY - w.scaleY) * 0.15;
      w.targetScaleX += (1 - w.targetScaleX) * 0.1;
      w.targetScaleY += (1 - w.targetScaleY) * 0.1;

      // ── Apply squash on direction change ──
      if (w.phase === "walking" && Math.abs(w.velocityX) > 0.01) {
        const speed = Math.sqrt(w.velocityX ** 2 + w.velocityY ** 2);
        const squashFactor = 1 + speed * 0.3;
        w.targetScaleX = 1 / squashFactor;
        w.targetScaleY = squashFactor;
      }

      // ── Secondary animations (Iteration 5) ──
      w.lineWobblePhase += WOBBLE_SPEED;
      // Tie and coat flutter - continuous subtle motion
      w.tieAngle =
        Math.sin(w.lineWobblePhase * 1.3) * 0.15 +
        Math.sin(w.lineWobblePhase * 2.1) * 0.08;
      w.coatFlutter =
        Math.sin(w.lineWobblePhase * 0.9) * 0.12 +
        Math.sin(w.lineWobblePhase * 1.7) * 0.06;
      w.pocketSway = Math.sin(w.lineWobblePhase * 1.1 + 0.5) * 0.1;

      // Idle fidgeting (Iteration 5)
      if (w.phase === "stopped") {
        w.fidgetTimer++;
        w.idleWobble = Math.sin(w.fidgetTimer * 0.15) * 0.02;
        if (w.fidgetTimer > MAX_FIDGET_DELAY && !w.fidgetAction) {
          const fidgetOptions = [
            "tie_adjust",
            "foot_shift",
            "head_tilt",
            "shoulder_shrug",
          ];
          w.fidgetAction =
            fidgetOptions[Math.floor(Math.random() * fidgetOptions.length)];
          w.fidgetTimer = 0;
          // Sound bubbles on some fidgets
          if (Math.random() < 0.3) {
            spawnSoundBubble(w.x, w.y - 20);
          }
        }
        if (w.fidgetTimer > 40) {
          w.fidgetAction = "";
          w.fidgetTimer = 0;
        }
      } else {
        w.fidgetAction = "";
        w.fidgetTimer = 0;
        w.idleWobble = 0;
      }

      // ── Age sound bubbles (Iteration 5) ──
      for (let i = soundBubbles.length - 1; i >= 0; i--) {
        soundBubbles[i].timer++;
        if (soundBubbles[i].timer >= soundBubbles[i].maxTimer) {
          soundBubbles.splice(i, 1);
        }
      }

      // ── Age footprints ──
      for (let i = footprints.length - 1; i >= 0; i--) {
        footprints[i].age++;
        if (footprints[i].age > PRINT_FADE) footprints.splice(i, 1);
      }

      // ── Bubble timer ──
      if (w.bubbleTimer > 0) w.bubbleTimer--;

      // ── Draw ──
      // Quest target (behind walker)
      drawQuestTarget(ctx);

      // Trail and particles (Iteration 3)
      drawTrail(ctx);
      drawParticles(ctx);

      // Sound bubbles (Iteration 5)
      drawSoundBubble(ctx);

      for (const fp of footprints) drawFootprint(ctx, fp);
      for (const p of portals) drawPortal(ctx, p);
      if (w.phase === "vertigo") drawStars(ctx, w.x, w.y, w.vertigoAngle);

      // Draw zombies (behind walker)
      for (const zombie of zombies) {
        drawZombie(ctx, zombie);
      }

      // Draw band members (behind walker)
      for (const member of bandMembers) {
        drawBandMember(ctx, member);
      }

      // Spawn sparkle particles from quest target while walking
      if (quest.active && w.phase === "walking" && Math.random() < 0.15) {
        spawnSparkle(
          quest.x + (Math.random() - 0.5) * 10,
          quest.y + (Math.random() - 0.5) * 10,
        );
      }

      drawPerson(
        ctx,
        w.x,
        w.y,
        w.legCycle,
        w.facing,
        w.fallAngle,
        w.getUpProgress,
        portalScale,
        currentActivity,
        w.activityCycle,
        w.scaleX,
        w.scaleY,
        w.idleWobble,
        w.fidgetAction,
      );

      // Emoji bubble
      if (w.bubbleTimer > 0 && w.bubble) {
        drawBubble(
          ctx,
          w.x,
          w.y,
          w.bubble,
          w.bubbleTimer,
          w.phase === "vertigo" ? 55 : 50,
        );
      }

      animHandle = requestAnimationFrame(animate);
    };

    const handleGridClick = (e: MouseEvent) => {
      const grid = getGrid();
      if (!grid || w.phase !== "walking") return;
      const rect = grid.getBoundingClientRect();
      const cx = e.clientX - rect.left;
      const cy = e.clientY - rect.top;
      if (Math.sqrt((cx - w.x) ** 2 + (cy - w.y) ** 2) < 25) {
        e.stopPropagation();
        w.phase = "falling";
        w.phaseTimer = 22;
        w.fallAngle = 0;
        w.bubble = "😱";
        w.bubbleTimer = 22;
        const angle = Math.atan2(w.y - cy, w.x - cx);
        w.velocityX = Math.cos(angle) * w.speed * 2;
        w.velocityY = Math.sin(angle) * w.speed * 2;
        w.fallSpots.push({ x: w.x, y: w.y });
        if (w.fallSpots.length > 8) w.fallSpots.shift();
      }
    };

    onMounted(() => {
      themeObserver = new MutationObserver(() => {
        themeColorCache.clear();
        COL = themedColor("--floor-plan-walker-ink");
        COL_DIM = themedColor("--floor-plan-walker-ink-dim");
      });
      themeObserver.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ["data-theme"],
      });
      animHandle = requestAnimationFrame(animate);
      const grid = getGrid();
      if (grid) grid.addEventListener("click", handleGridClick, true);
    });
    onUnmounted(() => {
      themeObserver?.disconnect();
      if (animHandle) cancelAnimationFrame(animHandle);
      const grid = getGrid();
      if (grid) grid.removeEventListener("click", handleGridClick, true);
    });

    return () => (
      <canvas
        ref={canvasRef}
        class="absolute inset-0 pointer-events-none"
        style="z-index: 4;"
      />
    );
  },
});
